export const en = {
  // ─── Language meta ───────────────────────────────────────────────────────
  lang: {
    en: "EN",
    ja: "日本語",
    "zh-HK": "繁中",
    "zh-TW": "繁中",
    "zh-CN": "简中",
    ko: "한국어",
    th: "ไทย",
    ru: "RU",
    current: "English",
  },

  // ─── Global nav ──────────────────────────────────────────────────────────
  nav: {
    platform: "Platform",
    forDesigners: "For Designers",
    forBrands: "For Brands",
    forManufacturers: "Production",
    forStudio: "Studio",
    caraAi: "Cara AI",
    pricing: "Pricing",
    more: "More",
    about: "About",
    academy: "Academy",
    accessPlatform: "Access Platform →",
    subline: "Where Design Meets Production",
    sublineBrand: "Source · Produce · Launch",
    sublineDesigner: "Your Runway for Jewelry",
    inspire: "Inspire",
    newsroom: "Newsroom",
    contact: "Contact",
    catalog: "Catalog",
    showcase: "Showcase",
    switchToDesigners: "For Designers →",
    switchToBrands: "For Brands →",
    howItWorks: "How It Works",
    overview: "Overview",
  },

  // ─── Footer ──────────────────────────────────────────────────────────────
  footer: {
    subline: "Designer Jewelry · B2B Sourcing · EPH Manufacturing",
    tagline: "\"The marketplace where exceptional design\nmeets flawless production.\"",
    accessPlatform: "Access Platform →",
    ephCertified: "EPH Certified Manufacturing",
    copyright: "Caratzon Ltd. All rights reserved.",
    columns: {
      platform: "Platform",
      company: "Company",
      resources: "Resources",
      contact: "Contact",
    },
    links: {
      forDesigners: "For Designers",
      forBrands: "For Brands",
      forManufacturers: "Production",
      caraAi: "Cara AI",
      tradeCatalogue: "Trade Catalogue",
      accessPlatform: "Access Platform",
      aboutCaratzon: "About Caratzon",
      ourStory: "Our Story",
      leadership: "Leadership",
      pressMedia: "Press & Media",
      investorRelations: "Investor Relations",
      howItWorks: "How It Works",
      ephHub: "EPH Manufacturing Hub",
      sustainabilityReport: "Sustainability Report",
      cadStandards: "CAD Standards",
      supplierCode: "Supplier Code of Conduct",
      academy: "Academy",
      inspire: "Inspire",
      newsroom: "Newsroom",
      aiCredits: "AI Credits",
      contactUs: "Contact Us",
      becomeManufacturer: "Become a Manufacturing Partner",
    },
    offices: {
      hongkong: "Design Gateway · 香港",
      guangdong: "Manufacturing Hub · 广东",
      london: "Europe Trade Desk",
    },
    legal: {
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      cookies: "Cookie Policy",
      supplierCode: "Supplier Code",
    },
  },

  // ─── Home page ───────────────────────────────────────────────────────────
  home: {
    meta: {
      title: "Caratzon — B2B Jewelry Sourcing · Pre-Engineered CAD · AI Marketing",
      description:
        "The premier B2B sourcing platform for independent fine jewelry. License pre-engineered CAD designs, manufacture sustainably at EPH, and generate marketing assets instantly with Caratzon Studio. Three IP tiers, production managed end-to-end.",
    },
    emergence: {
      tagline: "Sketch to shelf. Design, license, manufacture — only Caratzon.",
      subtitle: "The B2B platform for jewelry design, licensing, and production",
      cta: "Browse the Catalogue",
      ctaSecondary: "How It Works",
    },
    process: {
      overline: "How It Works",
      steps: [
        { number: "01", heading: "Discover", body: "Browse pre-engineered originals from independent designers worldwide.", hook: "No R&D costs. No NRE fees." },
        { number: "02", heading: "License", body: "Choose your IP tier and acquire commercial rights in seconds.", hook: "Marketing assets included with every license." },
        { number: "03", heading: "Produce", body: "Designs route into EPH's sustainable manufacturing hub. Done.", hook: "Zero factory management. Full traceability." },
      ],
    },
    storyBeat: {
      line1: "Sketch to shelf.",
      line2: "Design, license, manufacture.",
      line3: "Only Caratzon.",
      splitLabel: "I am a",
      splitDesigner: "Designer",
      splitBrand: "Brand Buyer",
    },
    showcase: {
      pieces: [
        {
          sku: "FD001-251225032",
          designer: "Ya Yu Studio",
          name: "Celestial Drape Necklace",
          descriptor: "Liquid gold, suspended in light.",
        },
        {
          sku: "FD016-251224017",
          designer: "Golden Hulu",
          name: "Fortune Gourd Pendant",
          descriptor: "Ancient symbol, modern silhouette.",
        },
        {
          sku: "FD033-251225049",
          designer: "Peony Atelier",
          name: "Bloom Drop Earring",
          descriptor: "Petals caught mid-fall.",
        },
        {
          sku: "FD051-251226013",
          designer: "Jade River Design",
          name: "Ripple Cuff Bracelet",
          descriptor: "Water on wrist, frozen in gold.",
        },
      ],
    },
    stats: [
      { value: "48+", numericValue: 48, label: "Original Designs" },
      { value: "12", numericValue: 12, label: "Independent Designers" },
      { value: "6", numericValue: 6, label: "Countries" },
      { value: "80%", numericValue: 80, label: "Designer Revenue Share", suffix: "%" },
    ],
    fork: {
      heading: "Now, where do you belong?",
      designer: {
        eyebrow: "For Designers",
        line: "Your craft deserves a global stage — not another Instagram post.",
        cta: "Enter as Designer",
      },
      brand: {
        eyebrow: "For Brands",
        line: "Skip R&D. License with marketing assets included. Launch today.",
        cta: "Enter as Brand",
      },
    },
    close: {
      line: "The next chapter starts here.",
      cta: "Get Started",
    },
    hero: {
      overline: "B2B Sourcing Platform · Sustainable Production via EPH",
      h1Line1: "Source the Design.",
      h1Line2: "Skip the R&D.",
      h1Line3: "Launch Instantly.",
      tagline:
        "The premier B2B sourcing platform for independent fine jewelry. License pre-engineered CAD designs, manufacture sustainably at our EPH hub, and let our AI Studio generate your marketing assets the second you click 'buy'.",
      cta: "Browse Trade Catalogue",
      ctaSecondary: "Learn About Caratzon Studio",
    },
    pillars: {
      overline: "Platform Capabilities",
      subtitle: "End-to-end workflow",
      items: [
        {
          heading: "The AI Marketing Engine (Caratzon Studio)",
          body: "Don't wait weeks for samples to shoot photos. Every license includes the Essentials Kit (e-comm shots, specs, and SEO copy). Upgrade to the Caratzon Kit to auto-generate lifestyle photography, social media assets, and PDF line sheets styled to your brand's exact colors.",
        },
        {
          heading: "Three IP Tiers, Legally Documented",
          body: "Standard (non-exclusive), Seasonal Embargo (12-month exclusivity), or Full Vault IP (permanent IP transfer). Legally binding commercial rights — not just a handshake.",
        },
        {
          heading: "End-to-End EPH Production",
          body: "Your order routes directly to our vetted EPH manufacturing network. Casting, setting, QC, and logistics are managed in one consolidated, sustainable hub. No factory negotiations required.",
        },
      ],
    },
    quote: {
      text: "\"Your next collection — no R&D, no agency, no factory to manage.\nSource to shelf, one platform.\"",
      attribution: "The Caratzon Premise",
    },
    marquee: {
      ariaLabel: "Catalogue preview",
    },
    howItWorks: {
      overline: "How It Works",
      subtitle: "Three steps",
      items: [
        {
          heading: "Discover",
          body: "Browse pre-engineered originals from independent creators. Preview the Essentials Kit for every piece — catalog shots, specs, editorial story, and an interactive 3D viewer — so you can evaluate the full package before you commit.",
        },
        {
          heading: "License & Generate",
          body: "Choose your IP tier (Standard, Embargo, or Vault) and acquire the commercial rights in seconds. The moment the license clears, Caratzon Studio generates your complete marketing kit so your sales team can start working immediately.",
        },
        {
          heading: "Produce via EPH",
          body: "Approved designs route into EPH's consolidated facility. Sustainable electroplating, certified eco processes, end-to-end milestone tracking — while your marketing assets are already live.",
        },
      ],
    },
    eph: {
      overline: "Manufacturing Partner",
      location: "China",
      headline: "China's Largest Sustainable Consolidated Jewelry & Eco Plating Manufacturing Hub",
      body: "Every production order routed through Caratzon is fulfilled at EPH's consolidated facility — casting, setting, polishing, and eco plating under one roof, with full environmental compliance and milestone traceability.",
      attrs: [
        {
          label: "Sustainable Electroplating",
          body: "Low-emission eco plating lines — gold, rhodium, and alloy finishing with verified reduced chemical waste and closed-loop water systems.",
        },
        {
          label: "Consolidated Facility",
          body: "Casting, setting, polishing, and plating under one roof. Fewer handoffs, tighter tolerances, faster cycle times.",
        },
        {
          label: "Direct Integration",
          body: "Approved designs on Caratzon route automatically into EPH's production queue. No manual re-sourcing, no disconnected supply chain.",
        },
        {
          label: "Full Traceability",
          body: "Every production phase logged and surfaced in your Caratzon dashboard — from raw metal receipt to final QC sign-off.",
        },
      ],
    },
    forWhom: {
      overline: "Who This Is For",
      heading: "Built for the\nTrade Buyer.",
      items: [
        {
          title: "Jewelry Retailers",
          desc: "Source designer-direct pieces your competitors can't find. Every license includes the Essentials Kit — catalog shots, specs, and SEO copy ready instantly. Upgrade to the Caratzon Kit for lifestyle photography and social assets. List on your website, drop into your catalog, and post on Instagram the same day you license.",
        },
        {
          title: "Boutique Owners",
          desc: "Skip the $1,500+ NRE (Non-Recurring Engineering) cost per design. License pre-tested, production-ready originals. Generate your Caratzon Kit and list new pieces on Shopify the exact same day you sign the license.",
        },
        {
          title: "Multi-brand Distributors",
          desc: "Source from dozens of global independent designers through one single vendor profile. Auto-generate perfectly formatted PDF wholesale line sheets to share with your retail accounts instantly.",
        },
      ],
    },
    caraAI: {
      overline: "Cara AI · Engineering Intelligence",
      heading: "The AI that designs for\nthe factory floor.",
      body: "Cara doesn't generate fantasy renders. It engineers jewelry that survives lost-wax casting, stone setting, and eight hours on a human hand — then generates a full bill of materials before you've finished your coffee.",
      stats: [
        { value: "±0.15mm", label: "Shrinkage compensation per alloy" },
        { value: "0.6mm", label: "Min. wall thickness enforced" },
        { value: "4-Prong+", label: "Stone retention engineered" },
        { value: "R≥0.4mm", label: "Edge radii for all-day comfort" },
      ],
      cta: "Try Cara AI — Live Demo",
      ctaSecondary: "Learn More",
    },
    inspirePreview: {
      overline: "Discover on Inspire",
      heading: "Scroll. Save. Source.",
      body: "Welcome to Caratzon Inspire — your B2B discovery feed. Browse a curated stream of the world's best 3D CAD designs. Build your brand's mood board, follow your favorite independent creators, and transition from inspiration to production in one click.",
      cta: "Explore Inspire",
    },
    designerBanner: {
      overline: "For Designers",
      heading: "Your CAD Files Deserve\na Better Portfolio.",
      body: "Join our curated network of global 3D creators. Upload your pre-engineered designs and automatically unlock a premium Caratzon Inspire Portfolio. Let our AI render your files for free, showcase your work to thousands of verified retail buyers, and earn high-margin royalties.",
      cta: "Claim Your Designer Portfolio",
    },
    footerCta: {
      overline: "Ready to Source",
      heading: "Your Next Collection.\nOne Platform. Zero Gaps.",
      cta: "Browse Trade Catalogue",
      tradeOnly: "Trade accounts only",
    },
  },

  // ─── For Designers page ──────────────────────────────────────────────────
  forDesigners: {
    meta: {
      title: "For Designers · Caratzon",
      description:
        "Your story, told by AI. Your designs, featured worldwide. Join Caratzon Inspire — upload your CAD, answer a few questions about your craft, and our AI writes your designer story. Earn 80% of every licensing fee with perpetual royalties.",
    },
    hero: {
      overline: "For Independent Jewelry Designers",
      h1Line1: "Your Story.",
      h1Line2: "Told Beautifully.",
      tagline:
        "Answer a few questions about your craft, your inspirations, and your vision. Our AI transforms your answers into a compelling designer narrative — and puts your work in front of global buyers.",
      cta: "Become an Inspiration →",
    },
    stats: {
      share: "Your share of every licensing fee",
      ipProtected: "IP Protected",
      cadNote: "CAD never transferred to buyers",
      perpetual: "Perpetual",
      royaltiesNote: "Royalties on every unit manufactured",
      zeroMarketing: "Zero Marketing",
      portfolioNote: "AI builds your story",
    },
    pain: {
      overline: "The Reality",
      p1: "You are a talented jewelry designer. Your pieces are original, beautifully resolved, and ready for production.",
      p2: "But to make a living, you are forced to be a social media manager, a copywriter, a lifestyle photographer, and a factory liaison. Your best work is sitting on a hard drive because you don't have the time or capital to market it to global B2B brands.",
      quote: "We built Caratzon so the world's most talented jewelry designers can focus on what they do best — design.",
      quoteAttribution: "— Caratzon founding team",
    },
    inspire: {
      overline: "Become an Inspiration",
      subtitle: "Your story, crafted by AI.",
      heading: "Seven questions.\nOne unforgettable story.",
      p1: "During onboarding, we ask you about your origin, your philosophy, your inspirations, and the emotional world behind your work. Not a form — a conversation about who you are as a creator.",
      p2: "Our AI takes your answers and crafts a magazine-quality designer profile — headline, narrative, thematic sections, and brand voice keywords. The kind of story that makes a buyer stop scrolling and reach out.",
      previewLabel: "AI-Generated Preview",
      previewTitle: "Tension — The World of Aria",
      previewBody: "She designs jewelry the way architects think about light — not to decorate, but to reveal.",
      previewExtended:
        "In a sun-flooded studio overlooking the Bosphorus, Aria Atelier works at the intersection of precision and wildness. Her pieces begin as watercolour sketches on textured paper — she needs to feel the weight before she touches the screen…",
      previewKeywords: ["tension", "gold", "architectural", "wabi-sabi", "quiet confidence", "Bosphorus light"] as readonly string[],
      cta: "Become an Inspiration →",
      features: [
        {
          label: "Your Origin, Your Voice",
          description: "We ask how you became a designer, what materials speak to you, and what cultural influences shape your aesthetic. Your words become the foundation of your public story.",
        },
        {
          label: "Philosophy & Inner World",
          description: "What should someone feel when they wear your jewelry? What creative tensions define your work? These deeper questions give the AI the psychological texture to write something that sounds like you, not a template.",
        },
        {
          label: "Published on Inspire",
          description: "Your AI-generated story goes live on your Inspire creator page — a public profile that buyers, press, and collaborators can discover. Edit it any time from your Studio dashboard.",
        },
      ],
    },
    designOfWeek: {
      overline: "Design of the Week",
      subtitle: "Featured globally. Chosen weekly.",
      heading: "Get featured in\nDesign of the Week.",
      badgeLabel: "Design of the Week",
      featuredTitle: "The Prismatic Petals Ring",
      featuredAttribution: "by Aria Atelier · Featured March 3, 2026",
      p1: "Every week, the Caratzon editorial team selects one outstanding design to spotlight across the platform. Featured designers get prime placement on the Inspire homepage, a dedicated editorial feature, and direct visibility to our entire buyer network.",
      p2: "Design of the Week isn't just exposure — it's a signal to buyers that your work has been recognized by the industry's most discerning curators. Past features have led to licensing deals, press coverage, and long-term brand partnerships.",
      benefits: [
        {
          label: "Homepage Spotlight",
          description: "Your design and story featured prominently on the Inspire homepage for seven days — the first thing every buyer sees when they visit the platform.",
        },
        {
          label: "Editorial Feature",
          description: "A dedicated editorial write-up crafted from your AI-generated story, expanded with context about the design's inspiration, materials, and manufacturing approach.",
        },
        {
          label: "Buyer Network Alert",
          description: "Every active buyer on the platform receives a curated notification featuring your design. Direct pipeline to licensing conversations.",
        },
      ],
    },
    platform: {
      overline: "How the Platform Works for You",
      subtitle: "Zero effort. Maximum reach.",
      items: [
        {
          heading: "AI Tells Your Story",
          body: "Answer questions about your craft during onboarding. Our AI writes your designer narrative, generates brand voice keywords, and publishes your Inspire creator page — a living portfolio that evolves with your work.",
        },
        {
          heading: "Direct Access to Global Buyers",
          body: "Your automated portfolio lands in front of production managers and buyers at major retail brands who are actively sourcing. You don't pitch them. They browse our curated showcase and find you.",
        },
        {
          heading: "Zero Manufacturing Headaches",
          body: "When a brand buys your design, you never speak to a factory. Our platform securely routes your file to vetted manufacturing partners. We handle casting, QC, and logistics.",
        },
        {
          heading: "Ironclad IP Protection",
          body: "Your CAD file is never shared with buyers — under any license type. Brands receive production rights only. Your source files go exclusively to our vetted manufacturing partners under NDA. Only a full IP acquisition transfers the design.",
        },
      ],
    },
    collections: {
      overline: "Collections + AI Storytelling",
      subtitle: "Your designs deserve more than a grid.",
      heading: "Group your pieces.\nAI tells the story.",
      p1: "Select two or more published designs, and our AI finds what connects them — shared materials, mood, creative sensibility — and builds a complete presentation around that thread.",
      p2: "Brands don't just see individual pieces. They see a designer with a coherent creative vision and a body of work that hangs together. That's what makes them reach out.",
      features: [
        {
          label: "One Voice Across Every Piece",
          description: "AI identifies the creative thread running through your designs — shared palette, mood, inspiration — so buyers see a coherent vision, not a random grid of files.",
        },
        {
          label: "Your Pieces Tell One Story",
          description: "A single narrative that names each design and draws connections between them. Buyers understand the thinking behind your collection before they even enquire.",
        },
        {
          label: "Every Collection Looks Vogue-Ready",
          description: "Magazine-grade showcase with a consistent editorial template — cover imagery, piece spotlights, and a unified visual feel across all your collections.",
        },
      ],
      cta: "Explore Collections in Studio →",
    },
    badges: {
      premium: "PREMIUM",
    },
    revenue: {
      overline: "Your Revenue, Three Ways",
      subtitle: "Three ways to get paid",
      heading: "Brands buy three ways.\nYou earn three ways.",
      footnote:
        "You keep 80% of every licensing fee across all three models. Caratzon retains 20% to cover platform operations, marketing, and manufacturing coordination. Standard and Seasonal Embargo licenses earn you perpetual royalties on every unit manufactured — your design keeps paying long after the initial deal. Full Exclusive Rights is a clean, one-time IP buyout: no ongoing royalties, just a single premium payment at a price floor you set.",
      models: [
        {
          tier: "Standard",
          label: "Non-Exclusive Production License",
          heading: "Standard License",
          description:
            "A brand licenses the right to produce your design through Caratzon's manufacturing network. Non-exclusive — multiple brands can hold a standard license simultaneously. You earn 80% of every licensing fee and a perpetual royalty on every unit manufactured.",
          benefit: "80% to you · Perpetual royalties · Design stays on market",
        },
        {
          tier: "Premium",
          label: "12-Month Exclusive Manufacturing Rights",
          heading: "Seasonal Embargo",
          description:
            "A brand pays a premium to embargo your design for 12 months. Your piece is hidden from the marketplace during their campaign window — no competitor can produce it. You earn 80% of the embargo fee plus perpetual royalties on every unit manufactured. When the window closes, your design returns to the open market.",
          benefit: "80% to you · Higher upfront · Time-limited · Fully repeatable",
        },
        {
          tier: "Vault",
          label: "Full Exclusive Rights",
          heading: "Full Exclusive Rights",
          description:
            "A brand acquires permanent ownership and receives the raw production files (.STL / .3DM) directly. The design is permanently removed from the marketplace. You receive 80% of a one-time premium you set yourself. Clean exit: one payment, design retired, no further obligations.",
          benefit: "80% to you · You set the floor · One-time clean exit",
        },
      ],
    },
    steps: {
      overline: "From Onboarding to Revenue",
      subtitle: "Three steps",
      items: [
        {
          heading: "Become an Inspiration",
          body: "Answer questions about your craft, your philosophy, and your inspirations. Our AI generates your designer narrative and publishes your Inspire page — your public creator profile that buyers discover. Upload your first CAD to complete your portfolio.",
        },
        {
          heading: "AI Builds Your Brand",
          body: "Our Story Lab generates studio-grade lookbook photography and editorial narrative for each piece. Group designs into collections and AI writes a unified story across all of them. Get selected for Design of the Week for maximum exposure.",
        },
        {
          heading: "Collect Revenue",
          body: "Brands discover your work and request a license. You approve or counter. Once confirmed, production routes automatically — 80% of the licensing fee is secured before a single gram is cast, with royalties flowing on every unit produced.",
        },
      ],
    },
    bottomLine: {
      overline: "The Bottom Line",
      p1: "Your job is to create. Our AI tells your story. Our platform sells your work.",
      p2: "From your first onboarding question to your first licensing deal — we handle the narrative, the photography, the buyer discovery, and the manufacturing. You just design.",
      shareLabel: "YOURS",
      shareNote:
        "80% of every licensing fee. Perpetual royalties per unit on Standard and Seasonal Embargo deals. A clean one-time premium you price yourself on Full Exclusive Rights acquisitions. Caratzon's 20% funds the marketing engine that sells your work while you sleep.",
    },
    cta: {
      overline: "Become an Inspiration",
      heading: "Your story.\nYour spotlight.",
      primary: "Become an Inspiration →",
      secondary: "Already a designer? Open Studio →",
    },
  },

  // ─── For Brands page ─────────────────────────────────────────────────────
  forBrands: {
    meta: {
      title: "For Brands · Caratzon",
      description:
        "Source original jewelry designs from independent creators — every license includes the Essentials Kit with catalog shots, specs, and SEO copy. Upgrade to the Caratzon Kit for AI-generated lifestyle photography, line sheets, and social media assets. Go to market the same day you license.",
    },
    howItWorks: {
      meta: {
        title: "How It Works — Caratzon for Brands",
        description: "From discovery to doorstep — browse designer originals, choose your license tier, and let our manufacturing network handle production while your marketing assets go live instantly.",
      },
      headline: "From Discovery\nto Doorstep.",
      intro: "Browse authenticated designer originals. Choose your license. Production routes through our manufacturing network while your marketing assets go live instantly. Here's exactly how it works.",
    },
    hero: {
      overline: "For Jewelry Brands · Boutiques · Distributors",
      h1Line1: "Your Next Collection.",
      h1Line2: "No R&D. No Agency.",
      h1Line3: "No Factory to Manage.",
      tagline:
        "Browse original designs from independent creators. License what you love — catalog shots, specs, and marketing assets ship with every piece. Production routes through our EPH network. Source to shelf, one platform.",
      ctaPrimary: "Browse Trade Catalogue",
      ctaSecondary: "See How It Works →",
    },
    marquee: {
      ariaLabel: "Catalogue preview",
    },
    badges: {
      fullIp: "FULL IP",
    },
    stats: {
      tiers: "Acquisition Tiers",
      designerDirect: "Designer-Direct",
      noIntermediaries: "No Intermediaries",
      contentReady: "Marketing Included",
      contentReadySub: "Essentials Kit with every license · Caratzon Kit upgrade available",
      productionManaged: "Production Managed",
      endToEnd: "End-to-End",
    },
    acquire: {
      overline: "Acquisition Models",
      subtitle: "Your collection, your terms",
      heading: "Three ways to acquire\na Caratzon design.",
      models: [
        {
          tier: "Standard",
          label: "Activation & Asset Fee",
          heading: "Standard License",
          description:
            "This is not just a permission slip — it's a shortcut worth multiples of the fee. You're buying a production-ready design and the Essentials Kit: catalog-ready shots, SEO product copy, and exact technical specs — everything you need to list the piece on your store the same day you sign. Upgrade to the Caratzon Kit for lifestyle photography, line sheets, and social media assets.",
          gets: [
            "Essentials Kit included — catalog-ready shots, SEO product story, and specs for your store, ready same day",
            "Upgrade to Caratzon Kit available — lifestyle photography, line sheets, and social media assets",
            "Instant manufacturing access — design unlocked in our production pipeline immediately",
            "Zero R&D costs — pre-engineered CAD vetted for flawless mass production",
            "Commercial rights — white-label under your own brand, indefinitely",
          ],
          benefit: "Essentials Kit included · Skip R&D · Launch same day",
        },
        {
          tier: "Embargo",
          label: "12-Month Exclusive Manufacturing Rights",
          heading: "Seasonal Embargo",
          description:
            "Lock the design down for your next campaign. Exclusive manufacturing rights for 12 months — your piece is hidden from the marketplace until your embargo expires. No competitor can produce it during your window. When the embargo lifts, the design returns to the market.",
          gets: [
            "Exclusive lock — zero competitor production during window",
            "All Standard License benefits included",
            "Essentials Kit included — exclusive catalog photography and story, yours alone for 12 months. Upgrade to Caratzon Kit available",
            "Design hidden from marketplace for your full embargo term",
          ],
          benefit: "Competitor-proof window · Exclusive content · Hero product potential",
        },
        {
          tier: "Vault",
          label: "Full Exclusive Rights",
          heading: "Full Exclusive Rights",
          description:
            "Own the IP forever. Acquire permanent ownership — the design is immediately removed from the marketplace. You receive the production-ready raw files (.STL / .3DM) directly upon payment. No other brand can ever source this design. This is the only scenario where source files are transferred.",
          gets: [
            "Permanent IP ownership — design retired from marketplace",
            "Raw production files (.STL / .3DM) transferred directly to you",
            "The Caratzon Kit included — skip the agency. Generate unlimited custom lifestyle photography, line sheets, and social kits with AI, styled specifically to your brand",
            "Price floor set by the designer — negotiate up from there",
          ],
          benefit: "Permanent exclusivity · Caratzon Kit included · CAD in hand",
        },
      ],
      whyStandard: {
        label: "Why the Standard License fee?",
        heading: "You're not paying for a permission slip. You're buying a massive shortcut.",
        points: [
          {
            heading: "Skip the NRE bill",
            body: "In traditional manufacturing, one design costs $500–$2,000+ in 3D design fees, wax prints, test castings, and revisions before you can run a single unit. The Standard License replaces all of that — the file is already engineered and stress-tested.",
          },
          {
            heading: "Essentials Kit — list on your store today",
            body: "Every Standard License includes the Essentials Kit: high-res catalog shots, SEO-optimized product descriptions, and exact technical specs — generated the moment you license. List the piece on your store the same day you sign. When you're ready for the full creative suite, upgrade to the Caratzon Kit for AI-generated lifestyle photography, branded line sheets, and ready-to-post social media assets.",
          },
          {
            heading: "White-label, no legal risk",
            body: "You get full commercial rights to sell under your own brand name, in your own packaging. The designer agrees not to come after you for copyright. Build your business on their art, risk-free.",
          },
        ],
      },
      ipNotes: {
        standardLabel: "Standard License & Seasonal Embargo",
        standardBody:
          "Production files are secured by Caratzon and released only to our vetted manufacturing partners under NDA. You never receive the raw CAD — production routes automatically through our network.",
        vaultLabel: "Full Exclusive Rights",
        vaultBody:
          "Upon payment, the designer's production-ready .STL / .3DM files are transferred directly to you. The design is permanently retired from the catalogue — no other brand can ever source it.",
      },
    },
    process: {
      overline: "How It Works",
      subtitle: "Discovery to delivery",
      items: [
        {
          heading: "Discover",
          body: "Browse authenticated designer originals. Preview the Essentials Kit for every piece — catalog shots, specs, editorial story, and an interactive 3D viewer — evaluate the full package before you commit.",
        },
        {
          heading: "License",
          body: "Choose your tier. Submit a sourcing request, negotiate terms directly with the designer, and lock your license. All documented and legally structured.",
        },
        {
          heading: "Produce",
          body: "Your order routes to our manufacturing network — casting, setting, QC, and logistics managed end-to-end. While production runs, your Essentials Kit is already live on your store. Upgrade to the Caratzon Kit and your lifestyle shots, line sheets, and social assets go live too. Promote before inventory arrives.",
        },
      ],
      quote: "\"Source original. Manufacture at scale.\nNo factory relationships required.\"",
      bullets: [
        "Vetted manufacturer network across Asia & Europe",
        "Casting, setting, QC, and logistics managed end-to-end",
        "Essentials Kit ships with every license — catalog shots, specs, and SEO copy",
        "Caratzon Kit upgrade: AI lifestyle photography, branded line sheets, and social media assets on demand",
      ],
      teaserItems: [
        { heading: "Discover", body: "Browse authenticated designer originals with full preview." },
        { heading: "License", body: "Choose your tier and lock your commercial rights." },
        { heading: "Produce", body: "Manufacturing managed end-to-end while your assets go live." },
      ],
      teaserCta: "See How It Works →",
    },
    vs: {
      overline: "Versus Traditional Wholesale",
      traditional: "Traditional",
      caratzon: "Caratzon",
      rows: [
        ["Provenance", "Unknown origin, multi-tier chain", "Direct from a verified independent designer"],
        [
          "R&D / NRE Cost",
          "$500–$2,000+ per design (3D designer, wax print, test cast, revisions)",
          "Zero — pre-engineered and vetted for flawless mass production",
        ],
        [
          "Time to Market",
          "Weeks of engineering + sampling before you can list",
          "License today, list on your store today — Essentials Kit included instantly",
        ],
        [
          "Marketing Assets",
          "Hire a photographer, stylist, and copywriter — weeks and thousands per collection",
          "Essentials Kit included instantly. AI-generated Caratzon Kits (lifestyle, social, line sheets) available on demand",
        ],
        [
          "Commercial Rights",
          "Negotiated separately, often unclear",
          "White-label rights included — sell under your own brand indefinitely",
        ],
        [
          "Exclusivity",
          "No mechanism to block competitors",
          "Standard, Seasonal Embargo (12-month), or Full Exclusive Rights (permanent)",
        ],
        [
          "Production",
          "Manage your own factory relationships",
          "Routes through our manufacturing network automatically",
        ],
      ],
    },
    studio: {
      overline: "Caratzon Studio",
      heading: "Source the Design.\nWe'll Generate the Marketing.",
      intro:
        "Sourcing new inventory used to mean waiting weeks for samples to arrive just to shoot photos. With Caratzon Studio, your marketing assets are generated the exact moment you acquire a license. Choose your launch strategy:",
      essentials: {
        heading: "The Essentials Kit",
        badge: "Included with every license",
        body: "Skip the data entry. Get production-ready specs, crisp catalog shots, and SEO copy instantly generated the moment you license a design. The perfect baseline to get your new inventory live on your store today.",
        features: [
          "High-res, white-background e-commerce extractions",
          "SEO-optimized product descriptions",
          "Exact technical and material specifications",
        ],
      },
      caratzonKit: {
        heading: "The Caratzon Kit",
        badge: "The Premium Launch Upgrade",
        body: "Skip the photo shoot. Replace an entire creative agency with one click. Get your logo, brand colors, and aesthetic seamlessly integrated into lifestyle shots, line sheets, and social media kits.",
        features: [
          "AI-generated lifestyle & on-model photography",
          "Auto-formatted PDF wholesale line sheets styled to your brand",
          "Ready-to-post social media assets (Reels/TikTok crops & captions)",
        ],
      },
    },
    caraAI: {
      overline: "Cara AI · Engineering Intelligence",
      heading: "Not a generative image tool.\nA jewelry engineering AI.",
      intro: "Most AI generates pretty pictures. Cara generates designs that survive the factory floor and feel right on the hand. The result isn't a mood board — it's a production-ready blueprint with a full bill of materials attached.",
      pillars: [
        {
          stat: "±0.15mm",
          label: "Shrinkage Compensation",
          body: "Cara factors in metal-specific shrinkage rates during lost-wax casting — gold, silver, platinum, brass — so the final piece matches the digital model, not a shrunken approximation.",
        },
        {
          stat: "0.6mm",
          label: "Min. Wall Thickness",
          body: "Every generated form respects alloy-specific minimum wall thicknesses — 0.6mm for platinum, 0.8mm for gold and silver. No paper-thin prongs. No collapsing bezels. Designs are structurally sound before they reach the caster.",
        },
        {
          stat: "4-Prong+",
          label: "Stone Tolerance Mapping",
          body: "Cara calculates prong placement, seat depth, and girdle clearance for every stone type and cut. Settings are engineered for secure retention under daily wear — not just visual plausibility.",
        },
        {
          stat: "R≥0.4mm",
          label: "Wearability & On-Hand Feel",
          body: "Cara evaluates band profile, inner curvature, weight distribution, and edge radii to ensure every piece feels as good as it looks. Sharp edges, top-heavy balance, and pinch points are flagged before the design leaves the screen.",
        },
      ],
      capabilities: [
        { label: "Casting Mechanics", detail: "Sprue placement, gate sizing, and flow analysis for defect-free casting" },
        { label: "Structural Analysis", detail: "Stress-tested wall thickness and load distribution checks across the entire form" },
        { label: "Bill of Materials", detail: "Accurate metal weight, stone count, and cost estimation from the concept stage" },
        { label: "Alloy Intelligence", detail: "Material-specific properties for 10K–24K gold, 925 silver, 950 platinum, and brass" },
        { label: "Production Readiness", detail: "Every output is evaluated against real EPH manufacturing tolerances" },
        { label: "Sketch-to-CAD Bridge", detail: "Upload a napkin sketch — Cara extracts geometry and engineers it for production" },
      ],
      closing: "Cara-engineered concepts are designed to cast right the first time — no rework, no wasted wax, no factory rejections. When a buyer sees a Cara concept, they're looking at a piece that has already been stress-tested against the same constraints their manufacturer will enforce. That's the difference between generative AI and engineering AI.",
      tryCara: "Try Cara AI — Live Demo",
      learnMore: "Learn More",
    },
    cta: {
      overline: "Start Sourcing",
      heading: "Browse the\nTrade Catalogue.",
      body: "Every Standard License includes the Essentials Kit — catalog shots, specs, and SEO copy, ready instantly. Upgrade to the Caratzon Kit for lifestyle photography, line sheets, and social media assets. Browse, evaluate in 3D, and go to market faster than any traditional pipeline.",
      primary: "Browse Designs",
      secondary: "See How It Works",
      tradeOnly: "Trade accounts only",
    },
  },

  // ─── For Manufacturers page ──────────────────────────────────────────────
  forManufacturers: {
    meta: {
      title: "Production · Caratzon",
      description:
        "Every Caratzon order is produced at EPH — China's largest consolidated jewelry manufacturing hub. 5,000+ manufacturers, eco-certified plating, full traceability from CAD to delivery.",
    },
    hero: {
      overline: "Production · EPH Manufacturing Hub",
      h1Line1: "One Facility.",
      h1Line2: "Every Capability.",
      h1Line3: "5,000+ Manufacturers.",
      tagline:
        "Every Caratzon order routes through EPH — China's largest consolidated jewelry manufacturing hub. 5,000+ manufacturers under one roof, licensed eco plating, and full traceability from CAD file to finished piece.",
      cta: "Browse the Catalogue",
    },
    stats: {
      cadReady: "5,000+",
      everyOrder: "Manufacturers Under One Roof",
      phases: "Full Chain",
      milestone: "Cast · Set · Polish · Plate · QC",
      ecoPlating: "Eco-Licensed",
      ephCertified: "Water Waste Treatment Facility",
      fullTrace: "Full Trace",
      clientVis: "Wax Print to Delivery",
    },
    orderContents: {
      overline: "How Your Pieces Get Made",
      subtitle: "From file to finished piece",
      items: [
        {
          heading: "CAD to Wax Print",
          body: "Your production file routes to EPH, where it's converted into a physical wax master — the first step from digital design to tangible piece.",
        },
        {
          heading: "Casting & Setting",
          body: "Lost-wax casting, stone setting, and assembly happen in the same facility. No cross-factory handoffs, no lost tolerances.",
        },
        {
          heading: "Eco Plating & Finishing",
          body: "Gold, rhodium, and alloy plating on licensed eco lines with on-site water waste treatment. Polishing and finishing to spec.",
        },
        {
          heading: "QC & Delivery",
          body: "Multi-point quality control before dispatch. Production milestones visible on your dashboard, waybill issued on completion.",
        },
      ],
    },
    workflow: {
      overline: "Production Phases",
      subtitle: "Your order, step by step",
      phases: [
        ["Order Confirmed", "Your design enters the EPH production queue"],
        ["NRE & Test Cast", "Wax print produced, sample submitted for approval"],
        ["Casting", "Metal poured, weight and alloy verified"],
        ["Setting & Assembly", "Stones set, tolerances checked, components assembled"],
        ["Plating & Finishing", "Eco-certified plating applied per your spec"],
        ["QC & Ship", "Final multi-point inspection, waybill issued"],
      ],
    },
    eph: {
      overline: "The EPH Manufacturing Hub",
      location: "China",
      headline: "5,000+ manufacturers. One roof. Full supply chain.",
      body: "EPH is China's largest consolidated jewelry manufacturing hub — and the exclusive production facility for every Caratzon order. With licensed water waste treatment, eco-certified plating lines, and the full production chain under one roof, EPH sets the benchmark for sustainable fine jewelry manufacturing at scale.",
      attrs: [
        {
          label: "Consolidated Supply Chain",
          body: "Casting, setting, polishing, and plating in a single facility. No cross-factory logistics, no lost handoffs.",
        },
        {
          label: "Licensed Eco Plating",
          body: "Gold, rhodium, and alloy finishing on eco-certified lines with on-site water waste treatment — verified reduced chemical discharge.",
        },
        {
          label: "Scale & Specialization",
          body: "5,000+ manufacturers with deep specializations across every jewelry category, from bridal to haute couture.",
        },
        {
          label: "Direct Digital Integration",
          body: "CAD files route directly from the Caratzon platform into EPH's production queue — zero manual re-entry, full traceability.",
        },
      ],
    },
    cta: {
      overline: "See the Catalogue",
      heading: "See What We\nCan Produce.",
      primary: "Browse the Catalogue",
      secondary: "Apply as a Manufacturer →",
    },
    inquiryForm: {
      overline: "Partner Application",
      heading: "Apply to Join the\nManufacturing Network.",
      subtitle:
        "Tell us about your facility and capabilities. Our partnerships team will review your application and respond within 3 business days.",
      companyName: "Company / Factory Name",
      contactName: "Primary Contact Name",
      email: "Business Email",
      phone: "Phone Number",
      website: "Company Website",
      location: "Factory Location (City, Country)",
      capabilities: "Manufacturing Capabilities",
      capabilitiesHint: "Select all that apply",
      capabilityOptions: [
        "Casting",
        "Stone Setting",
        "Plating",
        "Polishing",
        "CAD/CAM",
        "CNC",
        "Assembly",
        "Engraving",
        "Enameling",
        "QC / Inspection",
      ],
      specializations: "Jewelry Specializations",
      specializationsHint: "Select all that apply",
      specializationOptions: [
        "Rings",
        "Earrings",
        "Necklaces",
        "Bracelets",
        "Bridal",
        "Men's",
        "Watches",
        "Bespoke",
      ],
      certifications: "Certifications",
      certificationsHint: "Select all that apply",
      certificationOptions: [
        "RJC",
        "ISO 9001",
        "ISO 14001",
        "Hallmark",
        "Fairmined",
        "BSCI / Sedex",
      ],
      productionCapacity: "Monthly Production Capacity (pieces)",
      capacityOptions: [
        "Under 500",
        "500 – 2,000",
        "2,000 – 10,000",
        "10,000 – 50,000",
        "50,000+",
      ],
      additionalInfo: "Additional Information",
      additionalInfoPlaceholder:
        "Anything else you'd like us to know — specialties, lead times, existing partnerships, etc.",
      submit: "Submit Application",
      submitting: "Submitting…",
      successHeading: "Application Received",
      successBody:
        "Thank you for your interest in joining the Caratzon manufacturing network. Our partnerships team will review your application and be in touch within 3 business days.",
      errorRequired: "Please fill in all required fields.",
      errorEmail: "Please enter a valid email address.",
      errorSend: "Something went wrong. Please try again or email us directly.",
    },
  },

  // ─── Partner Application page ────────────────────────────────────────────
  partnerApplication: {
    meta: {
      title: "Partner Application · Caratzon",
      description:
        "Apply to join the Caratzon manufacturing network. Tell us about your facility, capabilities, and certifications.",
    },
  },

  // ─── Contact page ──────────────────────────────────────────────────────
  contact: {
    meta: {
      title: "Contact Us · Caratzon",
      description:
        "Get in touch with the Caratzon team. Speak with our Trade Desk, connect as a designer, or send a general inquiry.",
    },
    overline: "Contact",
    heading: "Get in Touch with Caratzon",
    subtitle:
      "Whether you are a retail brand looking to source exclusive designs, or a 3D CAD engineer wanting to monetize your IP, our global team is ready to assist you.",
    tabs: {
      brand: "I am a Brand / Retailer",
      designer: "I am a Designer",
      general: "General / Press / Investors",
    },
    brand: {
      headline: "Speak with our Trade Desk",
      copy: "Ready to skip the R&D and streamline your manufacturing? Connect with our sourcing experts to discuss platform access, enterprise volume, or our EPH manufacturing capabilities.",
      firstName: "First Name",
      lastName: "Last Name",
      workEmail: "Work Email Address",
      companyName: "Company / Brand Name",
      companyWebsite: "Company Website",
      helpWith: "What can we help you with?",
      helpOptions: [
        "Upgrading to Source / Atelier Tier",
        "Enterprise Volume Sourcing",
        "EPH Manufacturing Capabilities",
        "Caratzon Studio AI Demo",
        "General Platform Question",
      ] as readonly string[],
      selectPlaceholder: "Select a topic…",
      message: "Message",
      messagePlaceholder: "Tell us about your sourcing needs, timeline, or any specific questions…",
      submit: "Send Message",
      submitting: "Sending…",
    },
    designer: {
      headline: "Join the Caratzon Designer Network",
      copy: "Monetize your 3D CAD jewelry designs through our global licensing marketplace. Upload your portfolio, set your terms, and let brands come to you.",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      portfolioUrl: "Portfolio / Behance / Instagram URL",
      cadSoftware: "Primary CAD Software",
      cadOptions: [
        "Rhino / Grasshopper",
        "Matrix / MatrixGold",
        "ZBrush",
        "Blender",
        "SolidWorks",
        "Other",
      ] as readonly string[],
      selectPlaceholder: "Select your primary tool…",
      designCount: "Approximate Number of Designs",
      designCountOptions: [
        "1–10",
        "11–50",
        "51–200",
        "200+",
      ] as readonly string[],
      designCountPlaceholder: "Select a range…",
      message: "Tell us about your work",
      messagePlaceholder: "Describe your design style, specializations, and what you're looking for in a licensing partnership…",
      submit: "Apply as Designer",
      submitting: "Sending…",
    },
    general: {
      headline: "General Inquiries",
      copy: "For press, partnerships, or investor relations, please reach out to our corporate team.",
      name: "Name",
      email: "Email Address",
      topic: "Topic",
      topicOptions: [
        "Press & Media",
        "Investor Relations",
        "Partnerships",
        "Other",
      ] as readonly string[],
      selectPlaceholder: "Select a topic…",
      message: "Message",
      messagePlaceholder: "How can we help?",
      submit: "Send Inquiry",
      submitting: "Sending…",
    },
    success: {
      heading: "Message Received",
      body: "Thank you for reaching out. A member of our team will respond within 24 hours. In the meantime, feel free to explore the platform.",
      backToHome: "Back to Home",
      explorePlatform: "Explore Platform →",
    },
    errors: {
      required: "Please fill in all required fields.",
      invalidEmail: "Please enter a valid email address.",
      sendFailed: "Something went wrong. Please try again or email us directly at hello@caratzon.com.",
    },
    offices: {
      heading: "Our Global Offices",
      hongkong: { city: "Hong Kong", label: "Asia-Pacific Trade Desk" },
      guangdong: { city: "Guangdong", label: "Manufacturing & Operations HQ" },
      london: { city: "London", label: "European Trade Desk" },
    },
  },

  // ─── About page ──────────────────────────────────────────────────────────
  about: {
    meta: {
      title: "About Caratzon — Our Story",
      description:
        "Caratzon is the global two-sided ecosystem bridging independent jewelry designers and world-class manufacturing. Learn how we're transforming the jewelry supply chain.",
    },
    hero: {
      overline: "About Caratzon · Est. 2024",
      h1Line1: "Where Brilliant",
      h1Line2: "Design Meets",
      h1Line3: "Flawless Execution.",
      tagline:
        "Caratzon was born from a single, lost email thread that nearly cost a jewelry brand their entire holiday season. Today, we are a global, two-sided ecosystem bridging brilliant independent designers and world-class, fully managed manufacturing.",
      lastUpdated: "Last updated February 2026",
    },
    stats: {
      overline: "Platform Scale",
      subtitle: "By the numbers",
      items: [
        { value: "10,000+", label: "Exclusive CAD Designs" },
        { value: "150+", label: "Vetted Manufacturing Partners" },
        { value: "1M+", label: "Production Nodes Orchestrated" },
        { value: "$50M+", label: "Generated for Creators" },
        { value: "0", label: "Black-Box Supply Chain Delays" },
      ],
    },
    origin: {
      overline: "Our Origin",
      subtitle: "Every platform has a genesis",
      p1: "Every day, Caratzon empowers creators to monetize their art and allows B2B brands to source fresh, exclusive collections — with the absolute certainty of a transparent, node-based supply chain.",
      p2: "We exist to eliminate the risks and friction of global sourcing. When a brand falls in love with a design on our platform, they don't have to figure out how to make it. Caratzon handles the CAD engineering, physical prototyping, multi-vendor logistics, quality control, and final delivery — end to end.",
      digitalDesign: "Digital Design",
      precisionCraft: "Precision Craft",
    },
    timeline: {
      overline: "The Caratzon Story",
      subtitle: "Four phases",
      phases: [
        {
          heading: "The Broken Chain",
          subheading: "Where the story begins",
          label: "The Problem",
          body: "It started with a brilliantly designed, multi-component piece that required custom-cut sapphires, precise lost-wax casting, and a specialized rhodium finish. Routing the CAD files to three different factories across two continents resulted in a massive delay — caused by a minor porosity issue that a factory tried to hide. It became glaringly obvious that the jewelry industry's reliance on PDF invoices, WhatsApp updates, and blind trust was unsustainable.",
        },
        {
          heading: "The Trapped Talent",
          subheading: "A market disconnect hiding in plain sight",
          label: "The Insight",
          body: "Simultaneously, we realized a massive disconnect in the market. Brilliant independent jewelry designers had hard drives full of breathtaking 3D CADs, but absolutely zero factory connections or capital to produce them. Meanwhile, major B2B retail brands were desperate for fresh aesthetics but terrified of the manufacturing risks involved in testing out unknown creators. Art and logistics were fundamentally at odds.",
        },
        {
          heading: "Forging the Operating System",
          subheading: "Engineering the central nervous system",
          label: "The Build",
          body: "We decided to build the ultimate bridge. We engineered a proprietary, modular, node-based platform that acts as the central nervous system for jewelry manufacturing. It was designed to link a technical designer's vision directly to external factories, track real-time P&L margins, and secure every technical spec — all while keeping individual vendors entirely siloed.",
        },
        {
          heading: "The Ultimate Marketplace",
          subheading: "Design meets flawless execution",
          label: "The Outcome",
          body: "Caratzon officially launched not just as a tracking tool, but as a curated designer marketplace backed by a fully managed manufacturing engine. Today, when a brand falls in love with a design on our platform, they don't have to figure out how to make it. With one click, Caratzon automatically routes that design through our vetted network, handling the CAD engineering, physical prototyping, and final quality control.",
        },
      ],
    },
    vision: {
      overline: "Vision & Mission",
      visionLabel: "Our Vision",
      visionBody:
        "To be the definitive global ecosystem where world-class jewelry design seamlessly meets flawless execution — transforming the fragmented supply chain into a transparent, highly profitable engine for creators and brands alike.",
      missionLabel: "Our Mission",
      missionBody:
        "To empower independent jewelry designers by connecting their art with global B2B brands — seamlessly backed by our fully managed manufacturing operating system. We exist to eliminate the risks and friction of global sourcing.",
    },
    howItWorks: {
      overline: "How It Works",
      subtitle: "One click. End to end.",
      steps: [
        {
          heading: "Designer Uploads",
          body: "Independent designers upload CAD files, set IP terms, and publish editorial stories. Their work is protected behind signed, expiring viewer URLs.",
        },
        {
          heading: "Brand Discovers",
          body: "Retail brands browse 10,000+ exclusive designs in the curated catalogue, previewing pieces in an interactive 3D viewer within the platform.",
        },
        {
          heading: "Caratzon Routes",
          body: "Upon agreement, our node-based engine automatically routes the design through our vetted manufacturing network — CAD engineering, prototyping, QC.",
        },
        {
          heading: "Delivered at Margin",
          body: "Real-time P&L visibility, milestone tracking, and final delivery — with zero black-box delays and full supply chain transparency throughout.",
        },
      ],
    },
    leadership: {
      overline: "Leadership",
      founderRole: "Founder & Chief Executive Officer",
      founderName: "[Founder Name]",
      founderBio:
        "A brief 1–2 sentence bio here about your background in jewelry design, supply chain management, and technology — and what drives the Caratzon vision.",
      tags: ["Jewelry Design", "Supply Chain", "Technology"],
    },
    supplyChain: {
      overline: "Supply Chain Architecture",
      heading: "Node-Based.\nFully Transparent.",
      items: [
        {
          title: "Every vendor siloed",
          desc: "Individual factory nodes operate independently. No vendor sees another's specs, pricing, or capacity data.",
        },
        {
          title: "Real-time P&L",
          desc: "Margin visibility at every production milestone — designers, brands, and managers see only the data relevant to their role.",
        },
        {
          title: "Immutable audit trail",
          desc: "Every CAD version, approval, and production event is timestamped and logged — the complete chain of custody at your fingertips.",
        },
      ],
    },
    quote: {
      text: "\"Art and logistics were fundamentally at odds.\nWe built the bridge.\"",
      attribution: "Caratzon · Founding Principle",
    },
    cta: {
      overline: "Partner with Caratzon",
      heading: "Build the Future\nof Fine Jewelry.",
      primary: "Browse the Catalogue",
      secondary1: "For Brands & Retailers →",
      secondary2: "For Independent Designers →",
    },
  },

  // ─── For Studio (marketing page) ──────────────────────────────────────────
  forStudio: {
    meta: {
      title: "Caratzon Studio · Where AI Meets the Jeweler's Bench",
      description: "The AI-powered design workspace for jewelry creators. Meet Cara — your design assistant that thinks in millimeters, Mohs hardness, and tensile strength.",
    },
    hero: {
      overline: "Caratzon Studio",
      h1Line1: "Your Designs",
      h1Line2: "Deserve an Atelier.",
      tagline: "Caratzon Studio is the AI-powered workspace where independent jewelry designers upload CAD, tell their story, and let the platform handle everything else — marketing, licensing, production routing, and now, intelligent design assistance.",
      cta: "Apply as a Designer →",
      ctaSecondary: "See Pricing →",
    },
    stats: {
      aiAssisted: "AI-Assisted",
      designTools: "Design Tools",
      stoneTypes: "50+",
      stoneLabel: "Stone & Alloy Database",
      phases: "6",
      phasesLabel: "Sketch-to-Sales Steps",
      renders: "Instant",
      rendersLabel: "Catalog Renders",
    },
    cara: {
      overline: "Meet Cara",
      subtitle: "From Sketch to Sales",
      heading: "From Napkin Sketch\nTo Global Sales.",
      p1: "Cara is the AI design assistant that turns a hand-drawn sketch into a production-ready, market-listed piece of jewelry — handling every step from concept clarification to catalog renders, storytelling, and trade listing.",
      p2: "Snap a photo of your sketch. Cara asks the right questions — materials, dimensions, stone placement — then renders a faithful concept that respects your original vision down to every curve and proportion.",
      pipeline: [
        { num: "01", label: "Sketch", heading: "Upload Your Sketch", body: "Photograph a hand-drawn sketch or napkin doodle. Cara's vision model analyzes silhouettes, proportions, stone placements, and decorative details — extracting your intent with surgical precision.", icon: "pencil" },
        { num: "02", label: "Clarify", heading: "Intelligent Q&A", body: "Before touching a single pixel, Cara asks clarifying questions — metal type, stone cuts, ring size, finish preferences. Your answers become the design brief that locks in fidelity.", icon: "message-circle" },
        { num: "03", label: "Render", heading: "Faithful AI Render", body: "Cara generates a photorealistic concept that replicates your sketch with extreme fidelity — same silhouette, same proportions, same stone placement. Iterate through conversation until it's perfect.", icon: "image" },
        { num: "04", label: "Save", heading: "Save to Studio", body: "One click saves the approved render into your Studio as a new design draft — complete with the original sketch, conversation history, and all design parameters for future refinement.", icon: "save" },
        { num: "05", label: "Story", heading: "The Making Narrative", body: "Your sketch and the entire Cara conversation become the raw material for an AI-generated 'Making Of' story — a compelling editorial narrative that traces the creative journey from first line to final render.", icon: "book-open" },
        { num: "06", label: "Sell", heading: "List & Earn", body: "Catalog renders, lookbook assets, and trade listings are generated automatically. Your design enters the global catalog where brands discover, license, and order — and you earn 80% on every transaction.", icon: "trending-up" },
      ],
      conversationDemo: {
        label: "Cara — Sketch-to-Concept",
        userSketch: "I sketched this cocktail ring — can you turn it into a real concept?",
        caraAnalysis: "I can see a bold asymmetric band with what looks like a tension-set center stone and tapered shoulders. Before I render, let me clarify a few things:",
        caraQuestions: [
          "What metal are you envisioning? The angular lines suggest platinum or white gold",
          "The center stone — is that an emerald cut? Approximately what carat weight?",
          "I see texture marks on the band — brushed finish or hammered?",
        ],
        userReply: "Platinum, 2ct emerald-cut emerald, brushed finish on the band.",
        caraConfirm: "Perfect. Rendering your concept now — same silhouette, same proportions, platinum with brushed finish and a 2ct emerald-cut emerald in tension setting…",
        toolLabel: "Rendering faithful concept…",
      },
      cta: "Try Cara in Studio →",
    },
    storyLab: {
      overline: "AI Story Lab",
      subtitle: "Automated Storytelling",
      heading: "Your Story,\nTold Beautifully.",
      p1: "Answer seven guided questions during onboarding and our AI generates a rich editorial-style designer story, profile page, and lookbook — the kind of content that used to require a copywriter, photographer, and art director.",
      p2: "Each designer gets a dedicated Inspire profile where buyers discover your work through AI-generated narratives, not generic product listings.",
      features: [
        { label: "Editorial Profiles", description: "AI-generated designer stories that read like magazine features — not template bios." },
        { label: "AI Lookbooks", description: "Cohesive visual narratives across your portfolio, generated automatically from your designs." },
        { label: "Collection Storytelling", description: "Group pieces into themed collections and let AI weave a narrative across the set." },
      ],
    },
    workspace: {
      overline: "Designer Workspace",
      subtitle: "Everything in One Place",
      heading: "Upload. Manage.\nEarn.",
      p1: "The Studio workspace is your command center — upload CAD files, track licensing revenue, manage collections, monitor analytics, and communicate with buyers. All from one dashboard.",
      features: [
        { num: "01", heading: "3D Viewer", body: "Buyers inspect your designs in full 3D before licensing. Your CAD files are never exposed — only renders and previews." },
        { num: "02", heading: "Revenue Dashboard", body: "Track licensing fees, royalties, and production runs in real time. 80% revenue share on every license." },
        { num: "03", heading: "IP Protection", body: "CAD files are vault-encrypted and never shown to buyers. Structured licensing tiers with clear terms." },
        { num: "04", heading: "Direct Messaging", body: "Communicate with buyers and production managers directly inside the platform." },
      ],
    },
    revenue: {
      overline: "Revenue Models",
      subtitle: "Three Licensing Tiers",
      heading: "Earn on\nYour Terms.",
      models: [
        { tier: "standard", label: "Standard License", heading: "Non-Exclusive", description: "Multiple buyers can license the same design. You keep 80% of the licensing fee plus perpetual royalties on every production run.", benefit: "80% + Perpetual Royalties" },
        { tier: "embargo", label: "Seasonal Embargo", heading: "12-Month Exclusive", description: "One buyer gets competitor-proof exclusivity for a year. Higher upfront payment. After 12 months, the design returns to Standard availability.", benefit: "80% + Premium Exclusivity Fee" },
        { tier: "vault", label: "Full Exclusive", heading: "Permanent IP Transfer", description: "One-time buyout with permanent IP transfer including the CAD file. The highest upfront payment. Caratzon Kit included.", benefit: "Full Buyout + Caratzon Kit" },
      ],
    },
    steps: {
      overline: "Getting Started",
      subtitle: "Three Steps",
      items: [
        { heading: "Apply & Onboard", body: "Complete the seven-question onboarding flow. Our AI generates your editorial profile, designer story, and Inspire page automatically." },
        { heading: "Upload & Create", body: "Upload CAD files. Use Cara to refine designs, run structural analysis, and generate catalog-ready renders. Organize pieces into collections." },
        { heading: "Earn & Grow", body: "Buyers discover, license, and order your designs. You earn 80% on every license. Production is handled by our EPH-certified manufacturing hub." },
      ],
    },
    cta: {
      overline: "Join the Studio",
      heading: "Design the Future.\nWe Handle the Rest.",
      primary: "Apply as a Designer →",
      secondary: "Explore the Catalog →",
    },
  },

  // ─── Studio ────────────────────────────────────────────────────────────────
  studio: {
    nav: { guide: "Guide", inbox: "Inbox", storyLab: "My Designs", collections: "Collections", cara: "Cara", publishing: "Publishing", admin: "Admin →", catalog: "← Catalog", analytics: "Analytics" },
    notifications: { heading: "Notifications", empty: "No notifications yet." },
    cara: {
      title: "Cara",
      subtitle: "AI Design Assistant",
      newSession: "New Design Session",
      placeholder: "Describe your vision or upload an inspiration image…",
      emptyTitle: "Meet Cara",
      emptyDescription: "Your AI design assistant — part master jeweler, part mechanical engineer. Describe your vision or upload an inspiration image to begin.",
      analyzing: "Analyzing inspiration…",
      rendering: "Generating design render…",
      structuralAnalysis: "Running manufacturability simulation…",
      calculatingBom: "Calculating bill of materials…",
      generatingVideo: "Generating turntable preview — this may take up to a minute…",
      processing: "Processing…",
      phases: {
        spark: "Spark",
        architect: "Architect",
        crucible: "Crucible",
        bench: "Bench",
      },
      artifacts: {
        title: "Artifacts",
        empty: "Artifacts will appear here as Cara generates them.",
        moodBoards: "Mood Boards",
        renders: "Design Renders",
        structuralAnalysis: "Structural Analysis",
        materialAnalysis: "Material Analysis",
        bom: "Bill of Materials",
        videos: "Videos",
      },
      sessions: {
        empty: "No sessions yet",
        emptyHint: "Start your first design session with Cara",
        untitled: "Untitled session",
      },
      errors: {
        streamFailed: "Something went wrong. Please try again.",
        rateLimited: "You're sending messages too quickly. Please wait a moment.",
      },
    },
    gettingStarted: {
      overline: "Designer Studio",
      title: "Getting Started",
      subtitle: "A visual walkthrough of the three core studio features — so you understand the end-to-end flow before you begin.",
      howItWorks: {
        title: "How It Works",
        steps: [
          {
            title: "Upload & Create",
            label: "My Designs",
            body: "Upload your CAD file and supporting assets, then let AI generate a publication-ready catalog story for your design.",
          },
          {
            title: "Respond to Requests",
            label: "Inbox",
            body: "Brands submit customization requests. You collaborate, negotiate terms, and deliver revised CAD files — all tracked in one thread.",
          },
          {
            title: "Curate Collections",
            label: "Collections",
            body: "Group your published designs into themed editorial collections. AI weaves their individual stories into a cohesive narrative.",
          },
        ],
      },
      storyLabWorkflow: {
        title: "Design Workflow",
        subtitle: "Two phases turn your CAD file into a published Inspire page.",
        phase1: {
          title: "Phase 1 — Design Draft",
          subtitle: "A five-step wizard captures everything the platform needs before AI takes over.",
          steps: [
            { title: "Identity", body: "Name your piece and write a brief description." },
            { title: "Pricing & Specs", body: "Set your base licensing price, metal weight, and stone count." },
            { title: "IP Protection", body: "Choose Standard, Seasonal Embargo, or Full Exclusive Rights." },
            { title: "Upload Assets", body: "Upload your production CAD, 3D viewer file, and hero render." },
            { title: "Review & Create", body: "Confirm everything and create the design draft." },
          ],
        },
        phase2: {
          title: "Phase 2 — AI Story",
          subtitle: "Six steps transform your draft into a catalog-ready story with AI-generated narrative and imagery.",
          steps: [
            { title: "Inspiration Keywords", body: "Upload reference photos; AI analyses mood, materials, and themes." },
            { title: "Select Keywords", body: "Choose the words that best capture the soul of your piece." },
            { title: "Generate Story", body: "Describe materials and intent; AI crafts the catalog narrative." },
            { title: "Refine Story", body: "Edit the AI draft — this text is what buyers read." },
            { title: "Generate Showcase", body: "AI builds the catalog spread with sections, headlines, and image slots." },
            { title: "Submit for Approval", body: "Lock edits and send to the review team." },
          ],
        },
      },
      afterSubmission: {
        title: "What Happens After Submission",
        body: "An admin reviews your story and showcase. You'll be notified of the decision — approved designs go live in the trade catalog, while rejected submissions return to you with feedback for revision.",
        steps: [
          { label: "Submitted", body: "Your design enters the review queue." },
          { label: "Reviewed", body: "An admin evaluates story quality and asset completeness." },
          { label: "Published", body: "Approved designs appear in the trade catalog for brands to discover." },
        ],
      },
      fileRequirements: {
        title: "File Requirements",
        subtitle: "Quick reference for accepted formats and size limits.",
        columns: { asset: "Asset", formats: "Formats", maxSize: "Max Size", required: "Required" },
        rows: [
          { asset: "Production CAD", formats: ".stl, .3dm, .step", maxSize: "50 MB", required: "Yes" },
          { asset: "3D Viewer", formats: ".glb", maxSize: "25 MB", required: "Yes" },
          { asset: "Hero Render", formats: ".jpg, .png, .webp", maxSize: "10 MB", required: "Yes" },
          { asset: "Reference Photos", formats: ".jpg, .png, .webp", maxSize: "5 MB each", required: "Optional" },
        ],
      },
      ipTiers: {
        title: "IP Tiers Explained",
        subtitle: "Three licensing models — choose the one that fits your goals.",
        tiers: [
          {
            title: "Standard License",
            body: "Your CAD stays with Caratzon, released only to vetted manufacturers under NDA. You earn 80% of each licensing fee plus perpetual royalties. Multiple brands can license simultaneously.",
          },
          {
            title: "Seasonal Embargo",
            body: "One brand holds exclusive rights for 12 months. The design is hidden during their window and returns to the marketplace when the embargo closes. Premium pricing applies.",
          },
          {
            title: "Full Exclusive Rights",
            body: "A brand acquires full IP permanently. Your production CAD transfers directly to them. One-time high-value transaction — the design is permanently removed from the marketplace.",
          },
        ],
      },
      cta: {
        title: "Ready to begin?",
        button: "Start Your First Design",
      },
    },
    storyLab: {
      overline: "Designer Studio",
      title: "My Designs",
      subtitle: "Upload designs, generate AI stories, and publish to your Inspire page.",
      newDraft: "New Design Draft",
      overlineLabel: "My Designs",
      cancel: "Cancel",
      continue: "Continue",
      back: "Back",
      stepOf: "Step {current} of {total}",
      keyboardHint: "Tip: Alt + Arrow keys to navigate steps",
      editing: "Editing…",
      draftInProgress: "Draft in progress",
      emptyTitle: "No designs yet",
      emptySubtitle: "Upload your first design to start building your Inspire portfolio.",
      newDesign: "New Design",
      emptyHint: "or select an existing design from the sidebar",
      batchUpload: "Batch Upload",
      batchUploadSubtitle: "Drop up to 20 jewelry photos — AI generates title, description, and keywords for each",
      batchProcessing: "Processing {count} pieces…",
      batchGenerate: "Generate {count} Designs",
      batchCreated: "{count} designs created",
      batchDuplicates: "{count} duplicates skipped",
      batchPossibleDuplicates: "{count} possible duplicates",
      batchFailed: "{count} failed",
      batchDuplicateExact: "Already uploaded as \"{title}\"",
      batchDuplicateNear: "May be similar to \"{title}\" ({confidence}% match)",
      batchViewInLab: "View in My Designs",
      batchDropHere: "Drop up to 20 jewelry photos here",
      batchDropHint: "AI will generate title, description, and keywords for each piece",
      celebrationTitle: "Design draft created!",
      celebrationSubtitle: "Your design is ready. Start building its story with keyword generation below.",
      selectDesign: "Select a design\nto continue",
      selectDesignHint: "Choose a draft from the panel, or create a new design.",
      sidebar: { newDesign: "+ New Design", designs: "Designs", noDesigns: "No designs yet" },
      steps: {
        identity: {
          title: "Name Your Piece",
          subtitle: "Give your design a title and a brief description that captures its essence. This is the first thing buyers will see.",
          designTitle: "Design Title",
          titleTip: "This becomes the public name on the Caratzon marketplace. Keep it evocative and memorable — buyers browse by title first.",
          titlePlaceholder: "e.g. Lunette Drop Earrings",
          titleHint: "Choose a name that captures the essence of your piece",
          description: "Brief Description",
          descriptionPlaceholder: "One or two sentences describing the piece and its character.",
          descriptionHint: "This appears in the catalog. Keep it evocative and concise.",
        },
        pricing: {
          title: "Pricing & Specifications",
          subtitle: "Set your base licensing price and provide material specifications. These help buyers evaluate costs and manufacturers prepare production quotes.",
          basePrice: "Base Price (USD)",
          basePriceTip: "This is your licensing fee. Brands pay this amount to use your design. You receive 80% after platform fees.",
          basePriceHint: "The licensing fee brands pay to use your design. You earn 80%.",
          metalWeight: "Metal Weight (grams)",
          metalWeightTip: "Approximate weight helps manufacturers estimate production costs and provide accurate quotes.",
          metalWeightHint: "Approximate weight of the metal in the design",
          stoneCount: "Stone Count",
          stoneCountHint: "Total number of stones set in the piece",
          howPricingWorks: "How pricing works:",
          howPricingBody: "Your base price is the design licensing fee. Manufacturing costs are quoted separately by vetted factories based on metal weight, stones, and complexity.",
        },
        licensing: {
          title: "Choose Your IP Protection",
          subtitle: "Select how brands can use your design. Each tier offers different protection levels and revenue models.",
          licensingTip: "Your IP tier determines how brands can use your design. Standard allows multiple licenses; Exclusive gives one brand temporary rights; Vault transfers full ownership.",
          standard: "Standard License",
          standardDesc: "Your CAD stays with Caratzon, released only to vetted manufacturers under NDA. You earn 80% of each licensing fee plus perpetual royalties.",
          standardDetail: "Best for designers who want ongoing passive income. Multiple brands can license simultaneously.",
          exclusive: "Seasonal Embargo",
          exclusiveDesc: "One brand holds exclusive rights for 12 months. Design is hidden during their window. Returns to market when the embargo closes.",
          exclusiveDetail: "Premium pricing for exclusivity. Brand pays a higher licensing fee for the embargo period.",
          vault: "Full Exclusive Rights",
          vaultDesc: "A brand acquires full IP permanently. Your production CAD transfers directly to them. You earn 80% of your acquisition price.",
          vaultDetail: "One-time high-value transaction. The design is permanently removed from the marketplace.",
          ipTransfer: "IP Transfer",
        },
        assets: {
          title: "Upload Your Assets",
          subtitle: "Upload the design files buyers and manufacturers need. Your production CAD is protected — only vetted manufacturers see it under NDA.",
          assetsTip: "Your production CAD is never shared with buyers. Only Caratzon-vetted manufacturers access it under strict NDA terms.",
          requiredFiles: "Required Files",
          optionalFiles: "Optional — Improves AI Story Quality",
          vaultNotice: "Full Exclusive Rights.",
          vaultNoticeDetail: "Your CAD will be transferred directly to the acquiring brand.",
          cadProtected: "CAD is IP-protected.",
          cadProtectedDetail: "Only Caratzon-vetted manufacturers receive it under NDA.",
          cadLabel: "Production CAD",
          cadLabelVault: "Production CAD for IP Transfer",
          cadHint: ".stl · .3dm · .step — your source file, never shared with buyers",
          viewerLabel: "3D Viewer Asset",
          viewerHint: ".glb — an export from your CAD for the interactive buyer preview",
          renderLabel: "2D Render / Hero Image",
          renderHint: ".jpg · .png · .webp — the primary catalog thumbnail",
        },
        review: {
          title: "Review & Create",
          submit: "Create Design Draft",
          submitting: "Creating…",
          identity: "Identity",
          pricingSpecs: "Pricing & Specs",
          licensingTier: "Licensing Tier",
          assets: "Assets",
          edit: "Edit",
          uploaded: "Uploaded",
          missing: "Missing",
          allRequired: "All required files must be uploaded before creating.",
        },
      },
      workflow: {
        featured: "Featured",
        revisionRequired: "Revision Required",
        revisionHint: "Address the feedback above, then resubmit.",
        noReason: "No reason provided.",
        step01: "Inspiration Keywords",
        step01Sub: "Upload reference photos and add context. AI analyses mood, materials, and themes to build a keyword palette.",
        additionalContext: "Additional Context",
        contextPlaceholder: "e.g. Art Deco influence, midnight blue sapphires…",
        generateKeywords: "Generate Keywords",
        regenerateKeywords: "Regenerate Keywords",
        analysingRefs: "Analysing references…",
        step02: "Select Keywords",
        step02Sub: "Choose the words that best capture the soul of this piece — they anchor the AI-generated story.",
        saveKeywords: "Save Selected Keywords",
        saving: "Saving…",
        step03: "Generate Story",
        step03Sub: "Describe materials, intent, and hidden meaning. AI crafts a narrative that appears on your catalog page.",
        pieceDescription: "Piece Description",
        describePlaceholder: "Describe the design",
        materialsColors: "Materials & Colours",
        materialsPlaceholder: "Fine silver, pavé diamonds…",
        initialSpark: "Initial Spark",
        sparkPlaceholder: "An image, memory, or mood…",
        hiddenSymbolism: "Hidden Symbolism",
        symbolismPlaceholder: "What meaning is encoded in this piece?",
        generateStory: "Generate Story",
        regenerateStory: "Regenerate Story",
        writingStory: "Writing your story…",
        step04: "Refine Story",
        step04Sub: "Review the AI draft. Edit the title and body freely — this text is what buyers read on the catalog page.",
        saveStory: "Save Story",
        step05: "Generate Showcase",
        step05Sub: "AI builds the catalog spread — section count, image slots, and headlines. All generated from your story.",
        currentLayout: "Current layout:",
        sections: "sections",
        generatedImages: "generated image",
        generatedImagesPlural: "generated images",
        regenerateWarning: "Regenerating replaces the current layout.",
        generateShowcase: "Generate Showcase",
        regenerateShowcase: "Regenerate Showcase",
        buildingShowcase: "Building showcase…",
        step06: "Submit for Approval",
        step06Sub: "Locks further edits until an admin reviews your story and showcase. You'll be notified of the decision.",
        awaitingReview: "Your design is with the review team. You'll receive a notification when a decision is made.",
        submitForApproval: "Submit for Approval",
        awaitingAdmin: "Awaiting admin review…",
        published: "Published",
        submitting: "Submitting…",
        viewPublished: "View published story",
      },
      upload: {
        dragOrClick: "Drag here or",
        clickToBrowse: "click to browse",
        maxSize: "Max {size} MB",
        required: "Required",
        uploading: "Uploading {name}…",
        replace: "Replace",
        retry: "Retry",
        demoPreLoaded: "Demo file pre-loaded",
        photoRefs: "Photo References",
        photoRefsHint: "— optional, improves AI quality",
        dragOrClickPhotos: "Drag or click to add reference photos",
        upToPhotos: "Up to {max} photos",
        remove: "Remove",
        inspirationNotes: "Inspiration Notes",
        inspirationPlaceholder: "A memory, atmosphere, emotional cue, or specific spark.",
      },
    },
    collections: {
      overline: "Designer Studio",
      title: "Collections",
      subtitle: "Group your published designs into curated collections with AI-generated editorial stories.",
      newCollection: "New Collection",
      emptyTitle: "Your first collection awaits",
      emptySubtitle: "Select two or more published designs to create a collection. AI will weave their stories together.",
      emptyHint: "or select an existing collection from the sidebar",
      celebrationTitle: "Collection created!",
      celebrationSubtitle: "Your collection is ready for the story workflow. Start with keyword generation below.",
      sidebar: { collections: "Collections", noCollections: "No collections yet" },
      designPicker: {
        title: "Collection Pieces",
        subtitle: "Choose published designs to include. You need at least 2 designs.",
        selected: "{count} selected",
        minRequired: "Select at least 2 designs",
        saveDesigns: "Save Pieces",
        saving: "Saving…",
      },
      create: {
        title: "Create Collection",
        collectionTitle: "Collection Title",
        titlePlaceholder: "e.g. Midnight Oceanic Series",
        description: "Brief Description",
        descriptionPlaceholder: "A few sentences about the collection theme.",
        selectDesigns: "Select Designs",
        selectDesignsHint: "Choose at least 2 of your published designs",
        inspirationNote: "Inspiration Note",
        inspirationNotePlaceholder: "What unites these pieces?",
        submit: "Create Collection",
        submitting: "Creating…",
      },
      draftNotInitialized: "Collection draft not initialized.",
      draftNotInitializedCta: "Back to Collections",
      workflow: {
        step00: "Collection Pieces",
        step00Sub: "Manage which designs belong to this collection.",
        step01: "Collection Keywords",
        step01Sub: "AI analyses all pieces together to find unifying themes, motifs, and mood.",
        additionalContext: "Additional Context",
        contextPlaceholder: "e.g. shared creative season, material palette, emotional thread…",
        generateKeywords: "Generate Keywords",
        regenerateKeywords: "Regenerate Keywords",
        analysingCollection: "Analysing collection…",
        step02: "Select Keywords",
        step02Sub: "Choose the words that best capture the soul of this collection.",
        saveKeywords: "Save Selected Keywords",
        saving: "Saving…",
        step03: "Generate Collection Story",
        step03Sub: "AI weaves the individual design stories into a cohesive collection narrative.",
        generateStory: "Generate Story",
        regenerateStory: "Regenerate Story",
        writingStory: "Writing collection story…",
        step04: "Refine Story",
        step04Sub: "Review and edit the generated collection narrative.",
        saveStory: "Save Story",
        step05: "Generate Showcase",
        step05Sub: "AI designs a multi-piece editorial showcase with images for each section.",
        currentLayout: "Current layout:",
        sections: "sections",
        generatedImages: "generated image",
        generatedImagesPlural: "generated images",
        regenerateWarning: "Regenerating replaces the current layout.",
        generateShowcase: "Generate Showcase",
        regenerateShowcase: "Regenerate Showcase",
        buildingShowcase: "Building showcase…",
        step06: "Submit for Approval",
        step06Sub: "Locks further edits until an admin reviews your collection story and showcase.",
        awaitingReview: "Your collection is with the review team. You'll receive a notification when a decision is made.",
        submitForApproval: "Submit for Approval",
        awaitingAdmin: "Awaiting admin review…",
        published: "Published",
        submitting: "Submitting…",
        viewPublished: "View published collection",
        revisionRequired: "Revision Required",
        revisionHint: "Address the feedback above, then resubmit.",
        noReason: "No reason provided.",
        featured: "Featured",
      },
    },
    inbox: {
      title: "Customization Inbox",
      subtitle: "Negotiate terms, review briefs, and upload revised CAD files.",
      overline: "Designer Studio",
      openRequests: "Open Requests",
      emptyTitle: "No active requests",
      emptySubtitle: "When a brand submits a customization request for one of your designs, it will appear here.",
      selectRequest: "Select a request from the sidebar to begin.",
      thread: "Thread",
      noMessages: "No messages yet.",
      updateStatus: "Update Status",
      uploadRevision: "Upload CAD Revision",
      revisionHint: "Move request to In Progress to enable CAD revision uploads.",
    },
    analytics: {
      overline: "Studio",
      title: "Design Analytics",
      subtitle: "Track how your designs perform — views, engagement, and audience reach.",
      cards: {
        totalViews: "Total Views",
        uniqueVisitors: "Unique Visitors",
        avgTimeOnPage: "Avg. Time on Page",
        quoteRequests: "Quote Requests",
      },
      table: {
        design: "Design",
        views: "Views",
        uniqueViews: "Unique",
        clicks: "Clicks",
        avgTime: "Avg. Time",
        scrollDepth: "Scroll Depth",
      },
      drillDown: {
        viewsTrend: "Views Trend (30 Days)",
        clickBreakdown: "Click Breakdown",
        referrerBreakdown: "Referrer Sources",
      },
      empty: "No analytics data yet. Once your designs are published and viewed, metrics will appear here.",
      seconds: "s",
    },
  },

  // ─── Catalog ──────────────────────────────────────────────────────────────
  catalog: {
    overline: "Trade Catalogue",
    resultCount: "{count} results",
    designCount: "{count} designs",
    emptyTitle: "The catalogue is being curated",
    emptySubtitle: "Designers are preparing their first collections. Check back soon.",
    searchEmptyTitle: "No matches found",
    searchEmptySubtitle: "Try a different query or browse all designs.",
    browseCta: "Browse All Designs",
  },

  // ─── Orders ───────────────────────────────────────────────────────────────
  orders: {
    overline: "Trade Workspace",
    title: "Sourcing Orders",
    subtitle: "Track active negotiations, review revised 3D proofs from designers, and approve submissions for production routing.",
    activeOrders: "Active Orders",
    emptyTitle: "No sourcing orders yet",
    emptyCta: "Browse trade catalogue →",
    selectOrder: "Select an order to view the negotiation thread.",
    latestRevision: "Latest CAD Revision — 3D Preview",
    awaitingRevision: "Awaiting designer's first CAD revision.",
    respondToDesigner: "Respond to Designer",
  },

  // ─── Admin ─────────────────────────────────────────────────────────────────
  admin: {
    brand: "Admin",
    nav: { workflows: "Workflows", publishing: "Publishing", users: "Users", academy: "Academy", developer: "Developer", queues: "Queue Monitor", catalog: "Catalog", studio: "Studio", analytics: "Analytics", catalogAdmin: "Catalog", catalogListings: "Trade Listings", inspireAdmin: "Inspire", storyLab: "My Designs" },
    users: {
      overline: "Admin",
      title: "User Management",
      subtitle: "Create, edit, and manage user accounts across all roles.",
      tabs: { users: "Users", createNew: "Create New" },
      stats: { total: "Total Users", active: "Active", suspended: "Suspended", designers: "Designers" },
      search: "Search by name or email…",
      allRoles: "All Roles",
      noMatch: "No users match your filters.",
      columns: { name: "Name", email: "Email", role: "Role", status: "Status", actions: "Actions" },
      statusActive: "Active",
      statusSuspended: "Suspended",
      edit: "Edit",
      suspend: "Suspend",
      reactivate: "Reactivate",
      roles: {
        DESIGNER: "Designer",
        B2B_BUYER: "Brand / Buyer",
        MANUFACTURER: "Manufacturer",
        PRODUCTION_MANAGER: "Production Manager",
        ADMIN: "Admin",
      },
      roleLabels: { DESIGNER: "Designer", B2B_BUYER: "Brand", MANUFACTURER: "Factory", PRODUCTION_MANAGER: "PM", ADMIN: "Admin" },
      create: {
        title: "Create Account",
        subtitle: "Add a new designer, buyer, manufacturer, or staff member.",
        name: "Full Name",
        namePlaceholder: "e.g. Aria Atelier",
        email: "Email Address",
        emailPlaceholder: "designer@example.com",
        password: "Password",
        passwordPlaceholder: "Min. 8 characters",
        role: "Role",
        submit: "Create Account",
      },
      invite: {
        title: "Send Invite Link",
        subtitle: "Generate a signup link for a specific email and role. Valid for 7 days.",
        email: "Email",
        emailPlaceholder: "new-designer@example.com",
        role: "Role",
        submit: "Generate Link",
        copied: "Copied!",
        copy: "Copy",
      },
      editDialog: {
        title: "Edit User",
        subtitle: "Update account details and role assignment.",
        name: "Name",
        email: "Email",
        role: "Role",
        save: "Save Changes",
        cancel: "Cancel",
        stripe: "Stripe",
        accountActions: "Account Actions",
        resetPassword: "Reset Password",
        deleteAccount: "Delete Account",
      },
      confirm: {
        suspendTitle: "Suspend User",
        suspendDesc: "Suspend {name}? They will lose access to the platform immediately.",
        suspendButton: "Suspend",
        reactivateTitle: "Reactivate User",
        reactivateDesc: "Reactivate {name}? They will regain full access.",
        reactivateButton: "Reactivate",
        deleteTitle: "Delete User",
        deleteDesc: "Permanently delete {name}? This action cannot be undone and all associated data will be removed.",
        deleteButton: "Delete Forever",
        cancel: "Cancel",
      },
      success: {
        created: "User account created successfully.",
        updated: "User details updated.",
        suspended: "User account suspended.",
        reactivated: "User account reactivated.",
        deleted: "User account deleted.",
        reset: "Temporary password generated.",
        invited: "Invite link generated.",
      },
      tempPasswordHint: "Copy this now — it won't be shown again.",
      dismiss: "Dismiss",
    },
    setup: {
      title: "Platform Setup",
      subtitle: "Create the first administrator account to get started.",
      steps: { authorize: "Authorize", credentials: "Credentials", designer: "Designer", confirm: "Confirm" },
      setupPasswordLabel: "Setup Password",
      setupPasswordPlaceholder: "Enter the setup password",
      setupPasswordHint: "This instance requires a setup password to create the first administrator account.",
      setupPasswordInvalid: "Invalid setup password.",
      emailLabel: "Email address",
      emailPlaceholder: "admin@company.com",
      nameLabel: "Full name",
      namePlaceholder: "Jane Smith",
      passwordLabel: "Password",
      passwordPlaceholder: "Minimum 8 characters",
      confirmPasswordLabel: "Confirm password",
      confirmPasswordPlaceholder: "Re-enter password",
      designerTitle: "Designer Account",
      designerSubtitle: "Create the first designer account so they can start uploading designs.",
      designerNameLabel: "Designer name",
      designerNamePlaceholder: "Aria Atelier",
      designerEmailLabel: "Designer email",
      designerEmailPlaceholder: "designer@company.com",
      designerPasswordLabel: "Designer password",
      designerPasswordPlaceholder: "Minimum 8 characters",
      reviewTitle: "Review",
      reviewSubtitle: "Confirm the accounts that will be created.",
      createAccount: "Create Accounts",
      back: "Back",
      next: "Next",
      passwordMismatch: "Passwords do not match.",
      passwordTooShort: "Password must be at least 8 characters.",
    },
    analytics: {
      overline: "Admin",
      title: "Platform Analytics",
      subtitle: "Platform-wide design engagement metrics and performance insights.",
      cards: {
        totalViews: "Total Views",
        uniqueVisitors: "Unique Visitors",
        avgTimeOnPage: "Avg. Time on Page",
        totalClicks: "Total Clicks",
      },
      filters: {
        designer: "Filter by Designer",
        dateRange: "Date Range",
        allDesigners: "All Designers",
      },
      leaderboard: "Top Performing Designs",
      zeroEngagement: "Needs Attention (Zero Engagement)",
      table: {
        design: "Design",
        designer: "Designer",
        views: "Views",
        uniqueViews: "Unique",
        clicks: "Clicks",
        avgTime: "Avg. Time",
        scrollDepth: "Scroll Depth",
      },
      drillDown: {
        viewsTrend: "Views Trend",
        clickBreakdown: "Click Breakdown",
        referrerBreakdown: "Referrer Sources",
      },
      empty: "No analytics data yet. Once designs are published and viewed, metrics will appear here.",
      seconds: "s",
    },
  },

  // ─── Chat / Messages ────────────────────────────────────────────────────────
  messages: {
    overline: "Communication",
    title: "Messages",
    subtitle: "Conversations with designers, production managers, and manufacturing partners.",
    emptyTitle: "No conversations yet",
    emptyCta: "Browse the catalog to get started",
    typeMessage: "Type a message...",
    send: "Send",
    translate: "Translate",
    translating: "Translating...",
    loadMore: "Load earlier messages",
    typing: "is typing...",
    typingMultiple: "are typing...",
    today: "Today",
    yesterday: "Yesterday",
    unread: "unread",
    newConversation: "New Conversation",
    participants: "Participants",
    quoteProposal: "Quote Proposal",
    statusUpdate: "Status Update",
    attachFile: "Attach file",
    accept: "Accept",
    counter: "Counter",
    connecting: "Connecting to chat...",
    estimatedDays: "Estimated delivery",
    days: "days",
    messageDesigner: "Message Designer",
  },

  // ─── Academy ──────────────────────────────────────────────────────────────
  academy: {
    meta: {
      title: "Academy — Caratzon",
      description: "Expert guides on jewelry manufacturing, brand building, and industry trends for designers and micro brands.",
    },
    hero: {
      title: "Caratzon Academy",
      subtitle: "Expert knowledge for jewelry designers and emerging brands — from CAD to commerce.",
    },
    categories: {
      all: "All",
      manufacturing: "Manufacturing",
      business: "Business",
      trends: "Trends",
      wholesale: "Wholesale",
      brand: "Brand",
      tech: "Tech",
      operations: "Operations",
    },
    readingTime: "min read",
    featured: "Featured",
    relatedArticles: "Related Articles",
    backToAcademy: "← Back to Academy",
    cta: {
      title: "Ready to bring your designs to life?",
      description: "Caratzon connects independent designers with certified manufacturers for seamless production.",
      button: "Start on Caratzon →",
    },
    rss: "RSS Feed",
    noArticles: "No articles yet. Check back soon!",
  },

  // ─── Newsroom ─────────────────────────────────────────────────────────────
  newsroom: {
    meta: {
      title: "Newsroom — Caratzon",
      description:
        "Latest news, press releases, and updates from Caratzon. Company announcements, product launches, partnerships, and industry insights.",
    },
    hero: {
      issueLabel: "Newsroom",
      title: "Latest from Caratzon",
      subtitle: "Company news, press releases, product updates, and industry insights.",
    },
    categories: {
      all: "All",
      company: "Company",
      product: "Product",
      partnership: "Partnership",
      industry: "Industry",
      press_release: "Press Release",
    },
    readingTime: "min read",
    featured: "Featured",
    relatedArticles: "Related Articles",
    backToNewsroom: "← Back to Newsroom",
    cta: {
      title: "Ready to bring your designs to life?",
      description:
        "Caratzon connects independent designers with certified manufacturers for seamless production.",
      button: "Start on Caratzon →",
    },
    noArticles: "No articles yet. Check back soon!",
    pressAssets: {
      meta: {
        title: "Press Assets — Caratzon Newsroom",
        description:
          "Brand logos, guidelines, and media resources for press and partners. Download Caratzon brand assets for editorial use.",
      },
      hero: {
        issueLabel: "Press Assets",
        title: "Press Assets",
        subtitle:
          "Brand resources for press, partners, and editorial use. Download logos and guidelines below.",
      },
      logos: {
        heading: "Brand Logos",
        primary: "Primary logo (light)",
        dark: "Primary logo (dark)",
        icon: "Icon only",
        stacked: "Stacked",
      },
      guidelines: {
        heading: "Brand Guidelines",
        colorPalette: "Color Palette",
        colorNotes:
          "Primary brand accent: Teal Sapphire (#005F6B). Deep India ink for text. Warm cream vellum for backgrounds. Gold leaf reserved for decorative use only.",
        typography: "Typography",
        typographyNotes:
          "Display: Cormorant Garamond, Cormorant SC. Body: Inter. Use display fonts for headlines and editorial accents; Inter for UI and body copy.",
      },
      contact: {
        heading: "Press Contact",
        description: "For media inquiries, press kits, and interview requests:",
        contactLink: "General inquiries →",
      },
    },
  },

  contentKit: {
    nav: "Caratzon Kits",
    meta: {
      title: "Caratzon Kits — Caratzon",
      description: "Create complete Caratzon Kits for your jewelry designs — model photography, lookbook shots, catalog images, social captions, and editorial stories.",
    },
    list: {
      title: "Your Caratzon Kits",
      subtitle: "Create marketing-ready model photography, visuals, copy, and lookbooks from your designs.",
      createNew: "New Caratzon Kit",
      empty: "No Caratzon Kits yet. Create your first one to get started.",
      status: {
        draft: "Draft",
        generating: "Generating",
        ready: "Ready",
        failed: "Failed",
      },
    },
    wizard: {
      step1: "Select Source",
      step2: "Generate Visuals",
      step3: "Generate Copy",
      step4: "Create Lookbook",
      step5: "Publish & Share",
      source: {
        title: "Choose your design source",
        fromCatalog: "From Catalog",
        fromCatalogDesc: "Select a design you've licensed from the marketplace.",
        uploadNew: "Upload Images",
        uploadNewDesc: "Upload your own jewelry photos.",
        titleLabel: "Piece Title",
        descriptionLabel: "Description",
        next: "Generate Visuals",
      },
      visuals: {
        title: "Your Generated Visuals",
        subtitle: "AI-enhanced product shot and lifestyle lookbook images.",
        generating: "Generating your visuals...",
        regenerate: "Regenerate",
        acceptAll: "Accept & Continue",
        catalogShot: "Product Shot",
        lookbookShot: "Lifestyle",
      },
      copy: {
        title: "Marketing Copy",
        subtitle: "AI-generated product description and social captions.",
        generating: "Writing your copy...",
        productDescription: "Product Description",
        brandStory: "Brand Story",
        socialCaptions: "Social Captions",
        regenerate: "Regenerate Copy",
        next: "Create Lookbook",
      },
      lookbook: {
        title: "Lookbook Preview",
        subtitle: "Your AI-assembled lookbook ready for download.",
        generating: "Assembling your lookbook...",
        template: "Template Style",
        regenerate: "Regenerate with Different Style",
        templates: {
          minimal: "Minimal",
          editorial: "Editorial",
          bold: "Bold",
          organic: "Organic",
        },
        next: "Publish & Share",
      },
      publish: {
        title: "Publish & Share",
        subtitle: "Download your Caratzon Kit or publish it to Discovery.",
        download: "Download",
        downloadAll: "Download Complete Kit",
        downloadImages: "Download Images (ZIP)",
        downloadCopy: "Download Copy (TXT)",
        downloadPdf: "Download Lookbook (PDF)",
        publish: "Publish to Discovery",
        publishDesc: "Make your discovery page public on Caratzon.",
        slugLabel: "Discovery URL",
        categoryLabel: "Category",
        materialsLabel: "Materials",
        keywordsLabel: "Style Keywords",
        heroLabel: "Hero Image",
        published: "Published!",
        viewDiscovery: "View Discovery Page",
      },
    },
    upsell: {
      title: "You're Out of Studio Credits",
      body: "Upgrade to Source for 1,500 Studio Credits every month — enough for 15 Caratzon Kits. Or top up instantly with a credit pack.",
      cta: "View Plans",
    },
  },

  concierge: {
    title: "Caratzon Concierge",
    subtitle: "Your B2B jewelry sourcing assistant",
    welcome: "Welcome! Ask me about our designs, licensing tiers, manufacturing, or anything about Caratzon.",
    inputPlaceholder: "Ask about designs, licensing, pricing...",
    disclaimer: "AI-powered. Responses may not be fully accurate.",
    quickActions: [
      "How does licensing work?",
      "I'm a designer — how do I join?",
      "What are the pricing plans?",
      "I'd like to speak with someone",
    ],
    leadForm: {
      title: "Connect with our team",
      description: "Share your details and our team will follow up within 24 hours.",
      name: "Your name",
      email: "Business email",
      company: "Company (optional)",
      message: "Briefly describe what you need help with...",
      submit: "Connect me with the team",
      successTitle: "You're connected",
      success: "Our team will reach out within 24 hours. You can continue chatting here in the meantime.",
    },
    handoffConfirm: {
      title: "Handoff requested",
      description: "We've notified our team with your request details. A team member will be in touch shortly. You can continue chatting here or reach us directly at trade@caratzon.com.",
    },
  },

  inspire: {
    meta: {
      title: "Inspire — Caratzon",
      description: "Explore stunning jewelry designs from designers and brands worldwide.",
    },
    explore: {
      title: "Inspire",
      subtitle: "Explore jewelry designs from creators worldwide.",
      allCategories: "All",
      noResults: "No pieces found in this category.",
    },
    profile: {
      pieces: "Pieces",
      totalViews: "Total Views",
    },
    detail: {
      by: "by",
      views: "views",
      materials: "Materials",
      share: "Share",
      viewProfile: "View Profile",
      cta: {
        title: "Create Your Own Inspire Page",
        body: "Join as a designer to showcase your work with AI-generated lookbooks, editorial stories, and a dedicated profile — all from your own Studio.",
        button: "Start Creating",
      },
    },
    saved: {
      title: "Saved Pieces",
      empty: "No saved pieces yet.",
      emptySubtitle: "Browse Inspire to find your favorites.",
      backToInspire: "Back to Inspire",
    },
    creatorOnboarding: {
      meta: {
        title: "Join Inspire as a Creator · Caratzon",
        description: "Tell us about your craft, your story, and your vision — so we can showcase you to the world.",
      },
      header: "Inspire · Creator Onboarding",
      stepOf: "Step {step} of {total}",
      back: "← Back",
      continue: "Continue →",
      steps: {
        profile: "Profile",
        styleTools: "Style & Tools",
        creativeDna: "Creative DNA",
        philosophy: "Philosophy",
        innerWorld: "Inner World",
        production: "Production",
        review: "Review",
      },
      profile: {
        title: "Your designer profile",
        subtitle: "This is how buyers and PMs will see you on the platform.",
        firstName: "First Name *",
        lastName: "Last Name *",
        studioName: "Studio / Brand Name",
        studioPlaceholder: "e.g. Studio Noir, Aria Atelier",
        location: "Location / City",
        locationPlaceholder: "e.g. Paris, France",
        photo: "Profile Photo",
        photoOptional: "(optional — AI enhanced)",
        photoHint: "Upload a headshot or candid photo. Our AI will enhance it with warm studio lighting and a clean background.",
        photoEnhancing: "Enhancing your photo with AI…",
        photoSaved: "Photo enhanced & saved ✓",
        photoChange: "Change",
        photoUpload: "Upload",
      },
      style: {
        title: "Your aesthetic & tools",
        subtitle: "We use this to match your work with the right buyer audiences.",
        aestheticLabel: "How would you describe your design aesthetic? *",
        aestheticHint: "Select all that apply",
        designStyleLabels: {
          "Minimalist": "Minimalist",
          "Vintage-inspired": "Vintage-inspired",
          "Bridal/Engagement": "Bridal / Engagement",
          "Men's": "Men's",
          "Bold/Chunky": "Bold / Chunky",
          "Trend-driven Fashion": "Trend-driven Fashion",
          "Everyday Fine": "Everyday Fine",
          "Maximalist": "Maximalist",
        } as Record<string, string>,
        cadLabel: "What CAD software do you primarily use? *",
        cadHint: "Select all that apply",
        formatsLabel: "What file formats can you provide?",
        formatsHint: "Select all that apply",
      },
      creative: {
        title: "Your creative DNA",
        subtitle: "Help us understand the roots of your craft. These answers shape how AI tells your story to buyers.",
        originLabel: "How did you become a jewelry designer? *",
        originHint: "Your origin story — what drew you to this craft? A childhood memory, a mentor, a turning point?",
        originPlaceholder: "I grew up watching my grandmother thread seed pearls onto silk in her workshop in Jaipur. The way light caught each bead felt like magic to me…",
        materialsLabel: "What materials do you feel most connected to?",
        materialsHint: "Select all that resonate",
        signatureMaterialLabels: {
          "Gold (yellow, rose, white)": "Gold (yellow, rose, white)",
          "Platinum": "Platinum",
          "Silver / Sterling": "Silver / Sterling",
          "Titanium": "Titanium",
          "Enamel": "Enamel",
          "Precious gemstones": "Precious gemstones",
          "Semi-precious stones": "Semi-precious stones",
          "Pearls": "Pearls",
          "Diamonds": "Diamonds",
          "Wood / organic materials": "Wood / organic materials",
          "Recycled metals": "Recycled metals",
          "Lab-grown stones": "Lab-grown stones",
          "Mixed media": "Mixed media",
        } as Record<string, string>,
        ritualsLabel: "Describe your creative ritual",
        ritualsHint: "What does your process look like? Do you sketch first, go straight to CAD, work with wax? Where do you work?",
        ritualsPlaceholder: "I always start with watercolour sketches on textured paper. I need to feel the weight and flow before I touch the screen…",
        culturalLabel: "What cultural or historical influences shape your work?",
        culturalHint: "Traditions, regions, eras, art movements — anything that feeds your visual language.",
        culturalPlaceholder: "Art Deco geometry, Mughal floral motifs, Japanese wabi-sabi imperfection…",
      },
      philosophy: {
        title: "Your design philosophy",
        subtitle: "These questions reveal how you think about your craft — the tensions, convictions, and choices that make your work yours.",
        oneWordLabel: "If you could describe your design philosophy in one word, what would it be? *",
        oneWordPlaceholder: "e.g. Tension, Reverence, Defiance, Fluidity",
        feelingLabel: "When someone wears your jewelry, what should they feel? *",
        feelingHint: "Not what they should look like — what emotional state should the piece evoke?",
        feelingPlaceholder: "Like they're carrying a secret — something precious and entirely their own. A quiet confidence that doesn't need to announce itself.",
        tensionLabel: "What creative tension defines your work?",
        tensionHint: "Every designer navigates tradeoffs. Ornament vs. restraint? Tradition vs. disruption? Perfection vs. rawness?",
        tensionPlaceholder: "I'm always pulled between precision and imperfection. I want every line to be intentional, but I leave room for the metal to surprise me…",
        opinionLabel: "What's an unpopular opinion you hold about jewelry design?",
        opinionHint: "Something you believe that most people in the industry would disagree with.",
        opinionPlaceholder: "I think symmetry is overrated. The most beautiful pieces have a deliberate asymmetry that makes them feel alive…",
      },
      innerWorld: {
        title: "Your inner world",
        subtitle: "These deeper questions help our AI understand who you are beyond the bench — so your designer story feels authentically you.",
        inspirationLabel: "What outside of jewelry inspires your designs? *",
        inspirationHint: "Architecture, music, nature, food, film, mathematics, a city, a feeling — anything.",
        inspirationPlaceholder: "The way brutalist concrete catches afternoon light. The mathematical spirals in nautilus shells. The silence of a Japanese rock garden…",
        emotionalLabel: "What emotional need does designing fulfill for you?",
        emotionalHint: "Why do you keep creating? What would be missing from your life without it?",
        emotionalPlaceholder: "It's the only time my mind goes quiet. When I'm deep in a design, the noise stops and there's just the piece and me…",
        legacyLabel: "What do you want your body of work to say about you in 20 years?",
        legacyHint: "Your legacy vision — what thread should connect everything you create?",
        legacyPlaceholder: "That I made pieces people didn't just wear but kept. Objects that became part of someone's story, passed down not for their value but for their meaning…",
        metaphorLabel: "If your design practice were a place, what would it look like?",
        metaphorHint: "A metaphor for your creative space — real or imagined.",
        metaphorPlaceholder: "A glass workshop on a cliff edge. Precise tools, wild view. Everything inside is ordered; everything outside is untamed…",
      },
      production: {
        title: "Production readiness",
        subtitle: "Manufacturers need CAD files optimised for physical production — prong thickness, metal weight, shrinkage tolerances.",
        cadOptimisedLabel: "Are your CAD files fully optimised for physical manufacturing?",
        optionYes: "Yes — my files are fully optimised for manufacturing",
        optionMostly: "Mostly — minor adjustments may be needed",
        optionNo: "No — I design for render/visualisation only",
        optionHelp: "I need help optimising my files",
        portfolioLabel: "Portfolio / Website",
        portfolioPlaceholder: "https://yourportfolio.com",
        socialLabel: "Social Profiles",
        socialOptional: "(optional — displayed on your Inspire page)",
        instagramPlaceholder: "@yourstudio or https://instagram.com/yourstudio",
        pinterestPlaceholder: "@yourstudio or https://pinterest.com/yourstudio",
        tiktokPlaceholder: "@yourstudio or https://tiktok.com/@yourstudio",
        sampleUploadsTitle: "Sample uploads",
        sampleUploadsBody: "You'll be able to upload CAD renders and finished piece photos directly from your Studio dashboard after completing your profile.",
      },
      review: {
        title: "Review your profile",
        subtitle: "Everything look right? Once approved, you'll be able to update these from your Studio settings.",
        aiEnhanced: "AI-enhanced portrait",
        labels: {
          name: "Name",
          studio: "Studio",
          location: "Location",
          aesthetic: "Aesthetic",
          cadSoftware: "CAD software",
          fileFormats: "File formats",
          productionReady: "Production ready",
          portfolio: "Portfolio",
          instagram: "Instagram",
          pinterest: "Pinterest",
          tiktok: "TikTok",
        },
        creativeProfile: "Your Creative Profile",
        creativeLabels: {
          originStory: "Origin story",
          materials: "Materials",
          philosophy: "Philosophy",
          wearerFeels: "Wearer feels",
          inspiration: "Inspiration",
          legacy: "Legacy",
        },
        storyGeneration: {
          title: "Generate your designer story",
          subtitle: "Our AI will craft a compelling narrative from your questionnaire answers — ready to publish on your Inspire page.",
          button: "Generate My Story",
          loading: "Crafting your narrative…",
          error: "Story generation failed",
          tryAgain: "Try again",
          refineLater: "Once approved, you can refine this story from your Studio dashboard.",
        },
        terms: {
          prefix: "I confirm the information above is accurate and I agree to Caratzon's",
          termsLink: "designer terms of service",
          and: "and",
          privacyLink: "privacy policy",
        },
        submit: "Submit Application →",
        submitting: "Submitting…",
      },
      errors: {
        nameRequired: "Please enter your first and last name.",
        agreeRequired: "You must agree to the designer terms to proceed.",
        submissionFailed: "Something went wrong submitting your application. Please try again.",
      },
      pending: {
        meta: {
          title: "Application Under Review · Caratzon",
          description: "Your designer application is being reviewed by our team.",
        },
        overline: "Private Beta",
        thankYou: "Thank you, {name}",
        thankYouGeneric: "Thank you",
        submitted: "Your application has been submitted successfully. Caratzon is currently in",
        privateBeta: "private beta",
        submittedSuffix: ", and our team reviews every designer application personally.",
        emailNotice: "We'll notify you by email once your account has been approved. This typically takes 1–2 business days.",
        backToHome: "Back to Home",
        exploreAcademy: "Explore Academy →",
        rejected: {
          overline: "Application Update",
          heading: "We couldn't approve your application at this time",
          body: "Thank you for your interest in Caratzon. Unfortunately, we're unable to approve your application right now. If you believe this is an error or would like to provide additional information, please reach out to our team.",
          feedbackLabel: "Feedback from our team",
          contactUs: "Contact Us",
          reapply: "Re-apply",
          reapplying: "Submitting…",
        },
      },
    },
  },

  caraChat: {
    empty: {
      title: "Meet Cara",
      description: "Your AI design assistant — part master jeweler, part mechanical engineer. Describe your vision, upload a sketch, or drop an inspiration image to begin.",
      sketchToConcept: "Sketch → Concept",
      photoToDesign: "Photo → Design",
      ideaToRender: "Idea → Render",
    },
    placeholder: {
      initial: "Describe your vision, upload a sketch, or drop an image for inspiration…",
      direction: "Tell Cara what direction to take — materials, style, era…",
      adjust: "Ask Cara to adjust the design, or request a structural analysis…",
      finalize: "Ready to finalize? Ask Cara to generate the catalog render and BOM.",
    },
    dragOverlay: "Drop sketch or reference image",
    scrollPill: "↓ New messages",
    sketchFallback: "Here's my sketch / reference image. Please analyze it.",
    error: "Something went wrong. Please try again.",
    regenerate: "Regenerate",
    suggestions: {
      custom: "Other…",
      customPlaceholder: "Type your answer…",
      send: "Send",
    },
    editMessage: "Edit message",
    editSend: "Send",
    editCancel: "Cancel",
    download: "Download",
    copyMessage: "Copy message",
    justNow: "Just now",
    minutesAgo: "{n}m ago",
    toolLabels: {
      generate_mood_board: "Mood Board",
      generate_concept_sketch: "Concept Sketch",
      generate_design_render: "Design Render",
      adjust_design: "Adjusted Render",
      generate_catalog_render: "Catalog Render",
      render_from_sketch: "Sketch Concept",
      analyze_sketch: "Sketch Analysis",
      analyze_inspiration: "Inspiration Analysis",
      reference_lookup: "Reference Lookup",
      structural_analysis: "Structural Analysis",
      calculate_bom: "Bill of Materials",
      material_recommendation: "Material Analysis",
      generate_turntable_video: "Turntable Video",
      generate_lookbook_image: "Lookbook Image",
      present_design_specs: "Design Specs",
      finalize_design: "Finalized Design",
      share_to_gallery: "Gallery Share",
    },
    toolLoading: {
      rendering: "Rendering…",
      sketchingConcept: "Sketching concept…",
      analyzingSketch: "Analyzing your sketch…",
      analyzingInspiration: "Analyzing inspiration…",
      lookingUpReference: "Looking up reference data…",
      generatingVideo: "Generating video — this may take up to a minute…",
      stylingLookbook: "Styling lookbook shot…",
      structuralAnalysis: "Running manufacturability simulation…",
      materialCheck: "Checking material compatibility…",
      calculatingBom: "Calculating bill of materials…",
      sharingToGallery: "Sharing to gallery…",
      preparingSpecs: "Preparing specs…",
      finalizingDesign: "Finalizing design…",
      processing: "Processing…",
    },
    toolCards: {
      moodBoard: "Mood Board",
      conceptSketch: "Concept Sketch",
      refinedSketch: "Refined Sketch",
      turntablePreview: "Turntable Preview",
      sketchAnalysis: "Sketch Analysis",
      inspirationAnalysis: "Inspiration Analysis",
      structuralAnalysis: "Structural Analysis",
      billOfMaterials: "Bill of Materials",
      materialAnalysis: "Material Analysis",
      designFinalized: "Design Finalized",
      sharedToGallery: "Shared to Gallery",
      errorFallback: "Something went wrong.",
      designRender: "Design Render",
      updatedRender: "Updated Render",
      catalogRender: "Catalog Render",
      conceptFromSketch: "Concept from Sketch",
      lookbookLabel: "Lookbook",
      saveToStudio: "Save to Studio",
      savedOpenStudio: "Saved — Open in Studio",
      submitBrief: "Submit Design Brief",
      briefSubmitted: "Brief submitted — View Orders",
      designTitlePlaceholder: "Design title…",
      saving: "Saving…",
      save: "Save",
      cancel: "Cancel",
      structural: {
        manufacturabilityReport: "Manufacturability Report",
        areasAnalyzed: "{n} area analyzed",
        areasAnalyzedPlural: "{n} areas analyzed",
        issuesFound: "ISSUES FOUND",
        warnings: "WARNINGS",
        allClear: "ALL CLEAR",
        pass: "pass",
        warning: "warning",
        warningPlural: "warnings",
        critical: "critical",
        recommendationLabel: "Recommendation:",
        addressCritical: "Address critical issues before proceeding to casting.",
        reviewWarnings: "Review warnings above — design is buildable with noted adjustments.",
      },
      bom: {
        costBreakdown: "Cost Breakdown",
        lineItems: "{n} line item",
        lineItemsPlural: "{n} line items",
        item: "Item",
        qty: "Qty",
        estCost: "Est. Cost",
        laborSetting: "Labor & Setting",
        totalEstimate: "Total Estimate",
      },
      material: {
        compatibilityCheck: "Compatibility Check",
        compatible: "COMPATIBLE",
        concerns: "CONCERNS",
        concernsLabel: "Concerns",
        recommendationsLabel: "Recommendations",
        allCompatible: "All materials are compatible for production.",
      },
      sketch: {
        sketchInterpretation: "Sketch Interpretation",
        needsInput: "NEEDS INPUT",
        stones: "Stones",
        filigree: "Filigree",
        engraving: "Engraving",
        designNotes: "Design Notes",
        needsClarification: "Needs Clarification",
      },
      inspiration: {
        inspirationBreakdown: "Inspiration Breakdown",
        colorPalette: "Color Palette",
        motifs: "Motifs",
        textures: "Textures",
        suggestedFinishes: "Suggested Finishes",
        suggestedForms: "Suggested Forms",
      },
      designSpecs: {
        weight: "Weight",
        stones: "Stones",
        stoneSingular: "Stone",
        finish: "Finish",
        stonesLabel: "Stones",
        dimensions: "Dimensions",
        manufacturable: "Manufacturable",
        manufacturabilityConcerns: "Manufacturability concerns",
      },
      finalize: {
        productionReady: "Production Ready",
        metal: "Metal",
        stones: "Stones",
        stoneSingular: "Stone",
        estRetail: "Est. Retail",
        cadHandoff: "Ready for CAD hand-off, casting, or vendor quote",
      },
      gallery: {
        nowInGallery: "Now in the Gallery",
        storyGenerating: "A design story is being generated — check the Gallery tab to see it.",
      },
      reference: {
        title: "Reference Data",
        casting: "Casting Specs",
        metals: "Metal Properties",
        stones: "Stone Durability",
        settings: "Setting Compatibility",
      },
    },
    starters: [
      { label: "Design a solitaire ring", text: "Design a modern solitaire engagement ring with a 1ct oval diamond in 18k rose gold" },
      { label: "Art Deco cocktail ring", text: "Create a bold cocktail ring inspired by Art Deco geometry with emeralds and onyx" },
      { label: "Minimalist tennis bracelet", text: "Design a minimalist tennis bracelet with alternating sapphires and diamonds in platinum" },
    ],
    buyerOverrides: {
      empty: {
        title: "Design with Cara",
        description: "Describe the jewelry you're looking for — style, materials, occasion — and Cara will bring your vision to life with production-ready renders.",
      },
      placeholder: {
        initial: "Describe the jewelry you're looking for — style, materials, occasion…",
        direction: "Tell Cara more about your preferences — materials, budget, quantity…",
        adjust: "Ask Cara to adjust the design, or explore different variations…",
        finalize: "Happy with the design? Submit it as a design brief for production.",
      },
      starters: [
        { label: "Custom engagement ring", text: "I'm looking for a custom engagement ring — oval diamond, modern setting, rose gold" },
        { label: "Branded collection piece", text: "I need a signature pendant for my brand — minimalist, 14k gold, under $200 retail" },
        { label: "Wedding band set", text: "Design a matching his-and-hers wedding band set in platinum with subtle detailing" },
      ],
    },
  },

  // ─── Pricing page (/brands/pricing) ───────────────────────────────────────
  pricing: {
    meta: {
      title: "Pricing · Caratzon",
      description: "Transparent pricing for brands on Caratzon — source pre-engineered jewelry designs, generate AI marketing kits, and manufacture sustainably.",
    },
    hero: {
      overline: "For Brands · Trade Membership",
      heading: "Stop paying $2,500 per design.",
      headingEm: "Start at $0.",
      body: "Your membership unlocks the full catalog, AI-powered marketing tools, and direct sourcing from independent designers. Design licenses are separate — you only pay for what you choose to produce.",
    },
    socialProof: [
      "48+ pre-engineered designs",
      "Designers from 6 countries",
      "AI kits generated in under 3 minutes",
      "Profitable from unit 1",
    ],
    billing: {
      monthly: "Monthly",
      annual: "Annual",
      savingActive: "Saving ~17%",
      savingPrompt: "Save ~17% annually",
    },
    tiers: {
      explorer: {
        name: "Discover",
        tagline: "See what's possible",
        description: "Browse the catalog, save favorites, and get 200 welcome credits to generate your first AI marketing kit — free, no card required.",
        features: [
          "Browse the full catalog — every designer, every piece",
          "Save designs to your shortlist",
          "200 welcome Studio Credits (one-time)",
          "Generate 2 Essentials Kits to see the quality firsthand",
          "Pick your quantity — we handle the rest, from CAD to delivery",
        ],
        cta: "Start Free",
      },
      professional: {
        name: "Source",
        tagline: "Direct access to designers",
        description: "Full sourcing access. Direct designer relationships. 1,500 credits/month — enough for 15 complete marketing kits or 150 quick-turn catalog shots.",
        mostPopular: "Most Popular",
        features: [
          "Everything in Discover, plus:",
          "1,500 Studio Credits every month",
          "Source directly from designers — no middleman",
          "Negotiate licensing terms via private messaging",
          "AI marketing kit: lifestyle photos, lookbook, social assets — ready in minutes",
          "Standard + Exclusive licensing tiers",
          "Filter by metal, stone, style, price, and availability",
        ],
        cta: "Start Source",
      },
      atelier: {
        name: "Atelier",
        tagline: "Your private design workshop",
        description: "Full intellectual property transfer via Vault licensing. Your dedicated production manager handles everything from CAD to delivery. 5,000 credits/month.",
        features: [
          "Everything in Source, plus:",
          "Vault licensing — full IP + CAD file transfer",
          "5,000 Studio Credits every month",
          "Dedicated production manager — your single point of contact",
          "Priority support — direct line, same-day response",
          "Early access to new designers before public listing",
          "Custom sourcing briefs — tell us what you need, we find it",
          "Volume licensing terms for multi-SKU orders",
          "Cara AI — design original jewelry with AI assistance",
        ],
        cta: "Start Atelier",
      },
      enterprise: {
        name: "Enterprise",
        tagline: "Scale without limits",
        description: "For retail chains and large distributors. Custom credit volume, API integrations, white-glove production management, and a dedicated account team.",
        customLabel: "Custom",
        customSublabel: "Tailored to your volume and requirements",
        features: [
          "Everything in Atelier, plus:",
          "Custom Studio Credit volume — matched to your catalog velocity",
          "API access — catalog, kits, and order management",
          "White-glove production management across multiple factories",
          "Dedicated account team with quarterly business reviews",
          "Custom SLA, onboarding, and training",
          "Multi-seat team access with role-based permissions",
          "Negotiated licensing terms and volume discounts",
          "Priority manufacturing queue — your orders ship first",
        ],
        cta: "Talk to Our Team",
      },
    },
    priceDisplay: {
      free: "Free",
      freeSublabel: "No credit card required",
      perMonth: "/mo",
      saveBadge: "(save ~17%)",
      orAnnualOption: "or {amount}/yr",
      billedAnnually: "Billed {amount} annually",
      licensesSeparate: "Design licenses billed separately",
    },
    getStarted: "Get Started",
    studioCredits: {
      overline: "Studio Credits",
      heading: "Fixed costs per action. No surprises.",
      body: "Every AI action — from catalog photography to design consultation with Cara — costs a fixed number of credits from your monthly allowance. Need more? Top-up packs are available instantly and never expire.",
      link: "View credit costs & top-up packs →",
    },
    roi: {
      overline: "The Numbers",
      heading: "What it really costs to launch a new SKU.",
      body: "Traditional sourcing buries margin in hidden costs and months of lead time. Caratzon collapses the entire pipeline.",
      traditionalLabel: "Traditional Sourcing",
      caratzonLabel: "Caratzon",
      rows: [
        { metric: "Time to Market", traditional: "4–6 months", caratzon: "3 weeks" },
        { metric: "R&D & CAD Modeling", traditional: "$800 – $2,500+", caratzon: "$0 — pre-engineered" },
        { metric: "Physical Sampling", traditional: "$500+ per round", caratzon: "$0 — AI-rendered previews" },
        { metric: "Marketing Photography", traditional: "$2,000+ per campaign", caratzon: "~$7 via Studio Credits" },
        { metric: "Break-Even Point", traditional: "~50 units sold", caratzon: "Unit 1" },
      ],
      note: "Pre-engineered CAD files. Digital twin sampling. AI-powered photography. Every design on Caratzon is profitable from the first unit.",
    },
    faq: {
      overline: "Common Questions",
      heading: "Everything you need to know.",
      items: [
        {
          q: "What's included in the platform fee vs. design licenses?",
          a: "Your membership gives you catalog access, AI-powered marketing tools (Studio Credits), and direct sourcing capabilities. Design licenses are separate — you only pay for designs you choose to produce. Think of the membership as your toolbox; licenses are the materials.",
        },
        {
          q: "What happens to unused Studio Credits?",
          a: "Monthly credits expire at the end of each billing cycle. This keeps pricing fair for everyone. If you regularly need more, upgrade your plan or purchase top-up packs — top-up credits never expire.",
        },
        {
          q: "Can I switch plans or cancel anytime?",
          a: "Yes. Upgrade instantly, downgrade at the end of your billing cycle. No lock-in contracts, no cancellation fees. Annual plans can be cancelled with a prorated refund for unused months.",
        },
        {
          q: "What does a design license actually include?",
          a: "Every license includes the pre-engineered CAD file (routed directly to production — you never handle the file), full commercial rights at your chosen tier (Standard, Exclusive, or Vault), and the Essentials Kit (catalog shots, specs, SEO copy). Caratzon Kits with lifestyle photography are generated using your Studio Credits.",
        },
        {
          q: "How does Vault licensing (IP transfer) work?",
          a: "Available on Atelier and Enterprise plans. Vault gives you permanent, exclusive ownership of the design — including the original CAD files. The designer receives a one-time buyout fee. Once vaulted, the design is removed from the public catalog. It's yours.",
        },
        {
          q: "Do I need to handle manufacturing myself?",
          a: "No. Every production order routes through our vetted EPH manufacturing network. Casting, setting, QC, and logistics are managed end-to-end. You approve milestones from your dashboard — we handle the rest.",
        },
      ],
    },
    bottomCta: {
      heading: "Ready to source differently?",
      body: "Start free with Discover. Upgrade when you're ready — no commitment.",
      button: "Browse the Catalog",
      link: "Need a custom plan? Let's talk.",
    },
  },

  // ─── AI page (/ai) ────────────────────────────────────────────────────────
  ai: {
    meta: {
      title: "Cara AI — Jewelry Engineering Intelligence · Caratzon",
      description: "Cara is a jewelry engineering AI that designs for the factory floor. Shrinkage compensation, structural analysis, bill of materials, and production-ready concepts — not fantasy renders.",
      socialDescription: "The AI that engineers jewelry for real manufacturing. Shrinkage, tolerances, casting mechanics, wearability — every concept is production-ready.",
    },
    hero: {
      headingLine1: "Not a generative image tool.",
      headingLine2: "A jewelry engineering AI.",
      ctaDemo: "Try Cara AI — Live Demo",
      ctaCredits: "View Credit Costs",
    },
    pillars: {
      label: "Engineering Constraints",
      headingLine1: "Every concept is stress-tested",
      headingLine2: "before it leaves the screen.",
    },
    capabilities: {
      label: "Capabilities",
      headingLine1: "From napkin sketch",
      headingLine2: "to production-ready blueprint.",
    },
    whatYouCanAsk: {
      label: "What You Can Ask",
      heading: "A conversation, not a prompt box.",
      groups: [
        {
          title: "Design Consultation",
          prompts: [
            "\"Analyze this sketch for manufacturability\"",
            "\"What's the minimum wall thickness for 18K white gold?\"",
            "\"Will this halo setting survive daily wear?\"",
          ],
        },
        {
          title: "Material Science",
          prompts: [
            "\"Compare platinum 950 vs 18K for this band profile\"",
            "\"What alloy should I use for a 0.8mm prong?\"",
            "\"Shrinkage factor for lost-wax casting in 14K yellow?\"",
          ],
        },
        {
          title: "Visual Generation",
          prompts: [
            "\"Render this concept in rose gold with a pavé band\"",
            "\"Generate a mood board for Art Deco engagement rings\"",
            "\"Create a 360° turntable video of this design\"",
          ],
        },
        {
          title: "Production Planning",
          prompts: [
            "\"Generate a bill of materials for this ring\"",
            "\"Estimate manufacturing cost in 18K with VS1 diamonds\"",
            "\"What's the production timeline for a 50-unit run?\"",
          ],
        },
      ],
    },
    creditsTeaser: {
      label: "Studio Credits",
      heading: "Transparent, fixed-cost AI.",
      body: "Every Cara action has a fixed credit cost — 5 credits for a message, 15 for an image render, 25 for a 360° video. No token metering, no surprises.",
      link: "View all credit costs & top-up packs →",
    },
    finalCta: {
      heading: "See it for yourself.",
      body: "Try the live demo — no account required. Upload a sketch, ask a question, and watch Cara engineer a production-ready concept.",
      ctaDemo: "Try Cara AI — Live Demo",
      ctaPricing: "See Pricing",
    },
  },

  // ─── Designer AI page (/designers/ai) ──────────────────────────────────────
  designerAi: {
    meta: {
      title: "Cara AI for Designers — Caratzon",
      description: "Validate your CAD for manufacturability, generate portfolio renders, 360° videos, and lookbook photography — all powered by Cara AI. Focus on designing; let Cara handle the engineering.",
      socialDescription: "The AI co-pilot for jewelry designers. Validate CAD, generate renders, build your portfolio — production-ready from day one.",
    },
    hero: {
      overline: "Cara AI · For Designers",
      headingLine1: "Design fearlessly.",
      headingLine2: "Cara handles the engineering.",
      body: "Cara is your AI co-pilot — a master jeweler and mechanical engineer rolled into one. Validate your CAD for manufacturability, generate studio-grade renders, and build a portfolio that makes buyers reach out. You focus on the creative work.",
      ctaDemo: "Try Cara AI — Live Demo",
      ctaStudio: "Open Studio",
    },
    pillars: {
      label: "What Cara Does for You",
      headingLine1: "From your CAD file",
      headingLine2: "to a production-ready portfolio.",
      items: [
        {
          stat: "CAD → Render",
          label: "Portfolio Generation",
          body: "Upload your .3DM or .STL and Cara generates studio-grade product shots, lifestyle renders, and 360° turntable videos — the kind of visuals that used to cost thousands per piece.",
        },
        {
          stat: "±0.15mm",
          label: "Manufacturability Check",
          body: "Cara analyzes your design against real casting tolerances — wall thickness, shrinkage compensation, prong strength, edge radii — and flags issues before a buyer ever sees it.",
        },
        {
          stat: "Auto BOM",
          label: "Bill of Materials",
          body: "Get an instant, accurate bill of materials — metal weight, stone count, alloy-specific costs — so you can price confidently and buyers see transparent production estimates.",
        },
        {
          stat: "Your Story",
          label: "AI Storytelling",
          body: "Cara writes the editorial narrative for each piece and collection. Not generic copy — a story drawn from your design philosophy, materials, and creative intent.",
        },
      ],
    },
    capabilities: {
      label: "Designer Capabilities",
      headingLine1: "Everything you need",
      headingLine2: "to go from file to featured.",
      items: [
        { label: "CAD Validation", detail: "Structural analysis against EPH manufacturing tolerances — wall thickness, undercuts, draft angles" },
        { label: "Product Renders", detail: "Studio-quality product shots in multiple angles, metals, and stone configurations" },
        { label: "360° Turntable Video", detail: "Animated turntable previews that buyers can embed in presentations and pitch decks" },
        { label: "Lookbook Photography", detail: "AI-generated lifestyle and on-model photography styled to your brand aesthetic" },
        { label: "Collection Narratives", detail: "Group designs into collections and Cara writes a unified editorial story across all pieces" },
        { label: "Material Intelligence", detail: "Alloy-specific guidance for 10K–24K gold, 925 silver, 950 platinum — shrinkage, hardness, finish" },
      ],
    },
    workflow: {
      label: "Your Workflow with Cara",
      heading: "Upload. Validate. Publish.",
      steps: [
        {
          title: "Upload Your CAD",
          body: "Drop your .3DM, .STL, or .STEP file into Studio. Cara reads the geometry and begins analysis immediately.",
        },
        {
          title: "Review & Refine",
          body: "Cara flags manufacturability issues, suggests fixes, and generates a full bill of materials. Chat with Cara to iterate — ask about alloys, tolerances, or alternative settings.",
        },
        {
          title: "Generate Your Portfolio",
          body: "One click generates product renders, lifestyle shots, a 360° video, and an editorial story. Your design goes from a raw file to a buyer-ready showcase.",
        },
        {
          title: "Publish to Inspire & Catalog",
          body: "Push your validated, rendered design to your Inspire creator page and the Trade Catalog in one step. Buyers discover your work with visuals and narrative already in place — ready to license.",
        },
      ],
    },
    closing: "Cara doesn't replace your creativity — it removes everything standing between your design and the buyer who needs to see it. No photographer. No copywriter. No factory liaison. Just your vision, production-validated and beautifully presented.",
    creditsTeaser: {
      label: "Studio Credits",
      heading: "Fixed-cost AI. No surprises.",
      body: "Every Cara action has a transparent credit cost — 5 credits for a message, 15 for a render, 25 for a 360° video. Credits are included in your plan, with top-ups available any time.",
      link: "View credit costs →",
    },
    finalCta: {
      heading: "Your designs deserve to be seen.",
      body: "Upload your first CAD, let Cara validate and render it, and publish to Inspire — all in under five minutes.",
      ctaDemo: "Try Cara AI — Live Demo",
      ctaStudio: "Open Studio",
    },
  },

  // ─── AI Credits page (/ai/credits) ────────────────────────────────────────
  aiCredits: {
    meta: {
      title: "Studio Credits — Caratzon",
      description: "Learn how Studio Credits work on Caratzon — fixed-cost AI actions for photo kits, design consultation, and more. No per-image fees.",
      socialDescription: "Fixed-cost AI actions for photo kits, design consultation, and more.",
    },
    hero: {
      label: "Studio Credits",
      heading: "Transparent, fixed-cost AI.",
      body: "Every AI-powered action on Caratzon costs a fixed number of Studio Credits. No per-image fees, no token metering, no surprises.",
    },
    howItWorks: {
      label: "How It Works",
      heading: "Earn, spend, top up.",
      steps: [
        { step: "01", title: "Earn credits", body: "Paid plans include a monthly credit allowance. Explorer accounts get 200 welcome credits to try things out." },
        { step: "02", title: "Spend on any action", body: "Photo kits, lookbooks, Cara AI consultations — each action has a fixed credit cost shown upfront." },
        { step: "03", title: "Top up when you need more", body: "Buy credit packs instantly. Top-up credits never expire — subscription credits reset each billing cycle." },
      ],
    },
    photoStudio: {
      label: "Photo Studio",
      heading: "Production-ready assets.",
      body: "Upload a design and get catalog-ready photography, line sheets, and marketing copy — all AI-generated in minutes.",
    },
    caraAi: {
      label: "Cara AI",
      heading: "Your design engineering partner.",
      body: "Cara is an AI jewelry engineer that understands metallurgy, stone setting, structural integrity, and manufacturing constraints. Ask anything — from material science to BOM estimates.",
      note: "A text message (5 credits) is charged when you send. Image and video tools are charged when the AI invokes them — you'll see the cost before it runs. A typical 10-message session with a few renders uses around 100–150 credits.",
    },
    monthlyAllowances: {
      label: "Monthly Allowances",
      heading: "Credits included with your plan.",
      tableHeaders: ["Plan", "Monthly Credits", "Welcome Credits", "Price"],
      rows: [
        { plan: "Discover", monthly: "—", welcome: "200", price: "Free" },
        { plan: "Source", monthly: "1,500", welcome: "—", price: "$149/mo" },
        { plan: "Atelier", monthly: "5,000", welcome: "—", price: "$399/mo" },
        { plan: "Enterprise", monthly: "Custom", welcome: "—", price: "Custom" },
      ],
      note: "Subscription credits reset each billing cycle — unused credits don't roll over. Top-up credits never expire.",
    },
    topUpPacks: {
      label: "Top-Up Packs",
      heading: "Buy extra, keep forever.",
      body: "Launching a big collection? Buy credits instantly — they never expire.",
      bestValue: "Best Value",
      creditsLabel: "credits",
      perCredit: "¢ per credit",
      buyPrefix: "Buy",
      buySuffix: "Credits",
      note: "Subscribing is always the best deal — subscription credits cost less per credit than top-ups.",
      compareLink: "Compare plans",
    },
    faq: {
      label: "Common Questions",
      heading: "Credits, explained.",
      items: [
        { q: "What happens if I run out of credits mid-conversation with Cara?", a: "Text messages are checked before sending — you'll see a prompt to top up. Image and video tools check before generating, so the AI will let you know if you need more credits before it runs an expensive action." },
        { q: "Do unused subscription credits roll over?", a: "No. Subscription credits reset at the start of each billing cycle. If you regularly need more, consider upgrading your plan or buying a top-up pack — top-up credits never expire." },
        { q: "Can a single Cara message cost more than 5 credits?", a: "Yes. The base message costs 5 credits, but if Cara generates images (15 each) or videos (25 each) during the response, those are charged incrementally. A typical design consultation with 2–3 renders runs about 50–70 credits total." },
        { q: "Are admins charged credits?", a: "No. Admin accounts are exempt from credit charges for internal testing and operations." },
        { q: "What's the difference between Essentials Kit and Caratzon Kit?", a: "Essentials Kit (10 credits) generates catalog-ready shots, specs, and SEO copy. Caratzon Kit (100 credits) includes everything in Essentials plus a full lookbook with lifestyle photography, editorial images, and social-ready assets." },
      ],
    },
    actions: {
      photoStudio: [
        { action: "Essentials Kit", detail: "Catalog shots, specs & SEO copy" },
        { action: "Caratzon Kit", detail: "Full photo studio — lifestyle photography, line sheets & social assets" },
        { action: "Single Photo Re-gen", detail: "Re-generate one photo you didn't love" },
        { action: "Lookbook PDF", detail: "Branded wholesale line sheet" },
      ],
      caraAi: [
        { action: "Message", detail: "Sketch analysis, Q&A, material science, structural review, or design consultation" },
        { action: "Sketch", detail: "Quick concept sketch from a text description" },
        { action: "Image Render", detail: "Faithful sketch-to-concept render, mood board, catalog shot, or design adjustment" },
        { action: "Lookbook Image", detail: "Editorial lookbook image for presentation decks" },
        { action: "360° Video", detail: "Turntable video of your design from every angle" },
      ],
    },
  },

  demo: {
    meta: {
      title: "Cara AI Demo · Caratzon",
      description: "Try Cara — the jewelry engineering AI that designs for the factory floor and the human hand. Shrinkage, tolerances, casting mechanics, wearability — every concept is production-ready. Live demo.",
    },
    splash: {
      eyebrow: "Jewelry Engineering AI",
      heading: "Meet Cara",
      subheading: "The engineering AI that designs jewelry the factory can build and the customer wants to wear — from the very first sketch.",
      cta: "Start Designing",
      enterHint: "Press {key} to begin",
      promptDivider: "or try a prompt",
      signIn: "Sign in",
      noAccount: "No account required",
      notSaved: "Conversations are not saved",
      fullAccess: "Full platform access →",
      namePlaceholder: "Your name (shown in gallery)",
      nameHint: "So we can credit your creation",
    },
    prompts: [
      { label: "Engagement ring", text: "Design a modern solitaire engagement ring with a 1ct oval diamond in 18k rose gold" },
      { label: "Art Deco cocktail ring", text: "Create a bold cocktail ring inspired by Art Deco geometry with emeralds and onyx" },
      { label: "Tennis bracelet", text: "Design a minimalist tennis bracelet with alternating sapphires and diamonds in platinum" },
      { label: "Sculptural cuff", text: "I want a sculptural gold cuff with organic flowing lines, no stones, just pure metalwork" },
    ],
    capabilities: [
      { label: "Sketch to Production Blueprint", desc: "Upload a napkin sketch — Cara engineers it for the factory floor" },
      { label: "Casting & Shrinkage Analysis", desc: "Metal-specific shrinkage compensation, wall thickness validation" },
      { label: "Stone Tolerance Mapping", desc: "Prong placement, seat depth, and girdle clearance — engineered" },
      { label: "Wearability & Comfort", desc: "Band profile, weight balance, edge radii — designed for all-day wear" },
    ],
    header: {
      brandLabel: "Cara AI",
      tabs: { create: "Create", gallery: "Gallery" },
      back: "← Back",
      fullAccess: "Get Full Access",
    },
    gallery: {
      loading: "Loading gallery…",
      emptyTitle: "No designs yet",
      emptyBody: "Be the first to create something. Designs generated during this demo session will appear here for everyone to see.",
      emptyAction: "Create the first design",
      sectionTitle: "Created at this event",
      countSingular: "{count} design generated by demo visitors",
      countPlural: "{count} designs generated by demo visitors",
      createYours: "Create yours",
      createdWith: "Created with Cara AI",
      generating: "Generating story…",
      viewStory: "View Story",
      toolLabels: {
        render: "AI Render",
        mood_board: "Mood Board",
        catalog_render: "Catalog Render",
      },
      fallbackTool: "Render",
      timeAgo: {
        justNow: "Just now",
        minutes: "{n}m ago",
        hours: "{n}h ago",
        days: "{n}d ago",
      },
    },
    story: {
      backToGallery: "← Back to Gallery",
      fromSketch: "From Sketch",
      theSketch: "The Sketch",
      sketchCaption: "Original sketch by {name}",
      theDetails: "The Details",
      detailType: "Type",
      detailMetal: "Metal",
      detailStyle: "Style",
      detailStones: "Stones",
      detailFinish: "Finish",
      detailWeight: "Weight",
      detailPrice: "Est. Price",
      structuralAnalysis: "Structural Analysis",
      theStory: "The Story",
      showcase: "Showcase",
      designJourney: "The Design Journey",
      theConversation: "The Conversation",
      moreMessages: "{n} more messages in this conversation",
      createdBy: "Created by",
      createdWith: "Created with Cara AI at {event}",
      eventName: "Live Demo",
      tryIt: "Try Cara Yourself →",
      artifactLabels: {
        render: "Design Render",
        catalog_render: "Catalog Render",
        concept_sketch: "Concept Sketch",
        mood_board: "Mood Board",
        structural_analysis: "Structural Analysis",
        material_recommendation: "Material Analysis",
        bom: "Bill of Materials",
        sketch_analysis: "Sketch Analysis",
        lookbook: "Lookbook",
        video: "Turntable Video",
        inspiration: "Inspiration Analysis",
        design_specs: "Design Specs",
        finalize_design: "Finalized Design",
        gallery_share: "Gallery Share",
      },
    },
    brands: {
      splash: {
        eyebrow: "Design Intelligence for Brands",
        subheading: "Describe your vision. See it rendered. Send it to production.",
        cta: "Start Exploring",
        namePlaceholder: "Your name or brand",
        nameHint: "So we can personalize your experience",
        fullAccess: "Start Your Trade Account →",
      },
      prompts: [
        { label: "Custom tennis bracelet", text: "Customize this tennis bracelet in rose gold for my spring collection" },
        { label: "Signature pendant", text: "Create a signature pendant with our brand's geometric motif — minimalist, 14k gold" },
        { label: "Wedding band options", text: "Show me options for a minimalist wedding band set at three price points" },
        { label: "Sketch to production", text: "I have a sketch of a cuff — can you make it production-ready?" },
      ],
      capabilities: [
        { label: "Customization Preview", desc: "See your design vision rendered in photorealistic detail" },
        { label: "Material & Finish Options", desc: "Explore metals, stones, and finishes — side by side" },
        { label: "Sample Pricing", desc: "Instant estimates for prototyping and small-batch production" },
        { label: "Production Timeline", desc: "From concept to delivery — see the full manufacturing path" },
      ],
      header: {
        fullAccess: "Start Trade Account",
      },
    },
  },

  // ─── Production Dashboard ─────────────────────────────────────────────────
  productionDashboard: {
    title: "Production Dashboard",
    tabs: {
      portfolio: "Portfolio",
      board: "Board",
    },
    zoom: "Zoom",
    day: "Day",
    week: "Week",
    month: "Month",
    search: "Search...",
    searchPlaceholder: "Search plans, designs, manufacturers...",
    keyboardShortcuts: "Keyboard shortcuts",
    groupBy: "Group by",
    status: "Status",
    phase: "Phase",
    dragDisabledInPhase: "Drag disabled in phase view",
    backToPortfolio: "Portfolio",
    subOrders: "sub-orders",
    unassigned: "Unassigned",
    noItems: "No items",
    densityCompact: "Compact view",
    densityComfortable: "Comfortable view",

    // Empty states
    empty: {
      portfolioTitle: "No active productions",
      portfolioDescription: "Plans created in the Production Planner will appear here once approved.",
      portfolioLink: "Open Production Planner",
      boardTitle: "No sub-orders",
      boardDescription: "No sub-orders match your filters.",
      boardLink: "View Portfolio",
      planTitle: "No sub-orders",
      planDescription: "This plan has no sub-orders yet.",
      planLink: "Open Production Planner",
    },

    // Filter dropdowns
    filters: {
      allStatuses: "All Statuses",
      allDesigners: "All Designers",
      allBuyers: "All Buyers",
    },

    // Command palette
    command: {
      noResults: "No results found.",
      productionPlans: "Production Plans",
      quickActions: "Quick Actions",
      goToPortfolio: "Go to Portfolio",
      goToBoard: "Go to Board",
      navigate: "Navigate",
      open: "Open",
      close: "Close",
    },

    // Kanban
    kanban: {
      blockedAndCancelled: "Blocked & Cancelled",
      columns: {
        PENDING: "Pending",
        QUOTED: "Quoted",
        ASSIGNED: "Assigned",
        IN_PROGRESS: "In Progress",
        QA: "QA",
        COMPLETED: "Completed",
        BLOCKED: "Blocked",
        CANCELLED: "Cancelled",
      },
    },

    // Gantt
    gantt: {
      today: "Today",
      criticalPath: "Critical Path",
      duration: "Duration",
      days: "days",
      manufacturer: "Manufacturer",
      dependsOn: "Depends on",
    },

    // Detail panel
    detail: {
      status: "Status",
      manufacturer: "Manufacturer",
      duration: "Duration",
      dependencies: "Dependencies",
    },

    // Hover card
    hover: {
      duration: "Duration",
      manufacturer: "Manufacturer",
    },
  },

  // ─── Buyer Milestone Tracker ──────────────────────────────────────────────
  buyerTracker: {
    title: "Production Progress",
    inProgress: "In Progress",
    pending: "Pending",
    milestones: {
      orderConfirmed: "Order Confirmed",
      designEngineering: "Design Engineering",
      sampleProduction: "Sample Production",
      approval: "Approval",
      manufacturing: "Manufacturing",
      finalFinishing: "Final Finishing",
      qualityCheckDelivery: "Quality Check & Delivery",
    },
  },
};
